import React, { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useTheme } from "../ThemeContext";
import { FaFacebook, FaLinkedin, FaInstagram, FaXTwitter, FaPinterest } from "react-icons/fa6";
import { useWebChat, updateChatHistoryTitle } from "./Usewebchat";
import FeedbackModal from "./FeedbackModal";
import FeedbackSuccessModal from "./FeedbackSuccessModal";
import PaymentModal from "./Paymentmodal";
import AccountSettings from "./Accountsettings";
import ChatHistory from "./Chathistory";
import { botpress, api } from "../config";
import { WEBCHAT_CONFIG, CHAT_AUTH_TOKEN, CHAT_API_BASE as CHAT_API } from "./webchatConfig";
import { preprocessMessage } from "../utils/textFormatting";
import { stripHtmlToText } from "../utils/masking";
import { getPlatformMediaError, putVideoFirst } from "../utils/platformMedia";
import { addConversationTitle, getConversationTitle } from "./Chathistoryapi";
import { customerAPI } from "../customerAPI";
import MessageContent from "./MessageContent";
import MessageActions from "./MessageActions";
import { createRoot } from 'react-dom/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { jwtDecode } from "jwt-decode";
import ComingSoon from "./ComingSoon";
import AskQuestionModal from "./AskQuestionModal";
import QuestionSuccessModal from "./QuestionSuccessModal";
import MyQuestions from "./MyQuestions";
import ResponseRenderer from "./ResponseRenderer";
import NotificationsModal from "./NotificationsModal";
import PerformanceMarketing from "./PerformanceMarketing";
import BrandManagement, { loadBrands } from "./BrandManagement";
import SchedulePostModal from "./SchedulePostModal";
import ChatInputBar from "./ChatInputBar";
import HeroVideoPanel from "./HeroVideoPanel";
import { postizAPI } from "../api/postizAPI";
import {
  clearPendingPayment,
  getPayPalOrderIdFromApprovalUrl,
  hasMatchingPendingPayment,
  storePendingPayment,
} from "../utils/paymentSession";
import MarketingStrategyDocument from "./MarketingStrategyDocument";
import ChannelSelectionModal from "./ChannelSelectionModal";
import OfflineMarketingPreview from "./OfflineMarketingPreview";
import OnlineAdsPreview from "./OnlineAdsPreview";
import CampaignStatusDashboard from "./CampaignStatusDashboard";
import SocialMediaPostPreview from "./SocialMediaPostPreview";
import PinterestBoardSelect from "./PinterestBoardSelect";

const N8N_PPT_WEBHOOK_URL = "https://workflow.igr-consulting.com/webhook/93de2559-3ff8-49d4-887f-edcf9e7e94bb";
const N8N_POST_WEBHOOK_URL = "https://workflow.igr-consulting.com/webhook/bd5238df-c5f6-4d35-aaed-90673dc55249";
const N8N_REPORT_WEBHOOK_URL = "https://workflow.igr-consulting.com/webhook/download-full-report";

const SUGGESTED_PROMPTS = [
  "Size the US EV market and list the top 5 players",
  "Create a 7-day LinkedIn content calendar for a B2B SaaS brand",
  "Draft an email campaign for a product launch",
  "Analyze the competitive landscape for D2C skincare in India",
  "Generate a marketing strategy for a new fintech app",
  "Suggest low-budget offline marketing ideas for a local café",
];

const transformStrategyResponse = (rawData) => {
  if (!rawData) return null;

  const sectionMapping = {
    'Executive Summary': 'executiveSummary',
    'Campaign Goals': 'campaignGoals',
    'Target Audience': 'targetAudience',
    'Competitive Landscape': 'competitiveLandscape',
    'Brand Positioning': 'brandPositioning',
    'Channel Strategy': 'channelStrategy',
    'Content Pillars': 'contentPillars',
    'Campaign Phases': 'campaignPhases',
    'Budget Allocation': 'budgetAllocation',
    'KPIs & Measurement': 'kpisAndMeasurement',
    'Risk Mitigation': 'riskMitigation',
  };

  const strategy = {};

  // Normalize to array
  const items = Array.isArray(rawData) ? rawData : [rawData];

  for (const item of items) {
    if (!item || typeof item !== 'object') continue;

    // Format 1: [{ body: [{ section, content }] }]  (old wrapped format)
    if (item.body && Array.isArray(item.body)) {
      for (const bodyItem of item.body) {
        if (bodyItem?.section && bodyItem?.content !== undefined) {
          strategy[sectionMapping[bodyItem.section] || bodyItem.section] = bodyItem.content;
        }
      }
      continue;
    }

    // Format 2: [{ section: "...", content: "..." }]  (current flat format)
    if (item.section && item.content !== undefined) {
      strategy[sectionMapping[item.section] || item.section] = item.content;
      continue;
    }

    // Format 3: [{ "Executive Summary": "..." }]  (key-value format)
    for (const [key, value] of Object.entries(item)) {
      if (key !== 'section' && key !== 'content' && value !== undefined) {
        strategy[sectionMapping[key] || key] = value;
      }
    }
  }

  console.log('Transformed strategy:', strategy);
  return Object.keys(strategy).length > 0 ? strategy : null;
};

// Helper for parsing text with URLs and Bold formatting
/** Convert markdown to clean plain text suitable for social media captions */
const stripMarkdownToText = (content = "") => {
  return String(content)
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, "")
    // Remove inline code markers, keep text
    .replace(/`([^`]+)`/g, "$1")
    // Convert inline headings to line breaks + plain text (e.g., "#### 1. Initial Capital" -> "\n1. Initial Capital")
    .replace(/\s*#{1,6}\s+/g, "\n")
    // Remove bold/italic markdown
    .replace(/\*\*\*(.+?)\*\*\*/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/___(.+?)___/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    // Convert markdown tables to simple text
    .replace(/^\|(.+)\|$/gm, (_, row) => row.split("|").map(c => c.trim()).filter(Boolean).join("  |  "))
    // Remove table separator lines
    .replace(/^[|\s\-:]+$/gm, "")
    // Convert markdown links to plain text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Remove images
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "")
    // Remove blockquotes
    .replace(/^>\s*/gm, "")
    // Remove horizontal rules
    .replace(/^[-*_]{3,}$/gm, "")
    // Add line breaks before numbered items (1. 2. 3. etc) that aren't already on a new line
    .replace(/(?<![\n\s])(\d+\.\s+)/g, "\n$1")
    // Normalize multiple blank lines to max 2
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
// Minimum length for masked content to ensure overlay fits
const MIN_MASKED_LENGTH = 600;


// The preprocessMessage and transposeTableBlock have been moved to ../utils/textFormatting.js

const PostGeneratingPlaceholder = ({ isDarkMode }) => (
  <div className="flex items-start gap-2 mb-6">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
      isDarkMode
        ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
        : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
    }`}>
      <svg className="w-4 h-4 text-cyan-500 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
    <div className="flex-1 max-w-[85%] md:max-w-[70%] space-y-2">
      <div className={`flex items-center gap-2 mb-2 text-sm font-medium ${
        isDarkMode ? 'text-gray-400' : 'text-gray-500'
      }`}>
        <span className="animate-pulse">Generating your post</span>
        <span className="flex gap-0.5">
          {[0,1,2].map(i => (
            <span key={i} className={`w-1 h-1 rounded-full ${
              isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
            } animate-bounce`} style={{ animationDelay: `${i * 150}ms` }} />
          ))}
        </span>
      </div>
      <div className={`h-52 w-full rounded-2xl overflow-hidden ${
        isDarkMode ? 'post-shimmer-dark' : 'post-shimmer-light'
      }`} />
      <div className={`h-4 w-3/4 rounded-full ${
        isDarkMode ? 'post-shimmer-dark' : 'post-shimmer-light'
      }`} />
      <div className={`h-4 w-1/2 rounded-full ${
        isDarkMode ? 'post-shimmer-dark' : 'post-shimmer-light'
      }`} />
    </div>
  </div>
);

const TypingIndicator = ({ isDarkMode }) => {
  const [phase, setPhase] = useState("Thinking...");

  useEffect(() => {
    // Cycle phases every 3 seconds
    const interval = setInterval(() => {
      setPhase((prev) => {
        if (prev === "Thinking...") return "Reviewing...";
        if (prev === "Reviewing...") return "Generating...";
        return "Thinking...";
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`flex items-center gap-3 p-3 px-4 rounded-2xl w-fit ${isDarkMode ? 'bg-gray-800/80 border border-gray-700/50' : 'bg-white border border-gray-200'} shadow-sm text-gray-500 transition-all duration-300`}>
      <div className="relative flex items-center justify-center">
        {/* SVG Spinner to ensure clear rotation instead of a border hack */}
        <svg 
          className={`animate-spin h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <div className="flex w-[100px]">
        <span className={`text-sm font-medium animate-pulse ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {phase}
        </span>
      </div>
    </div>
  );
};

const PdfExportMessage = ({ message, conversationTitle }) => {
  const exportContent = preprocessMessage(message?.content || '');

  return (
    <div className="bg-white text-slate-900 w-[1024px] px-14 py-12 font-sans">
      <div className="border-b border-slate-200 pb-6 mb-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700 mb-3">Infinium Report</p>
            <h1 className="text-3xl font-semibold leading-tight text-slate-900">
              {conversationTitle || 'Conversation Export'}
            </h1>
          </div>
          <div className="text-right text-sm text-slate-500">
            <p>Generated</p>
            <p className="font-medium text-slate-700">{new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {exportContent ? (
          <ResponseRenderer content={exportContent} isDarkMode={false} isExport={true} />
        ) : (
          <p data-pdf-block="true" className="text-base leading-7 text-slate-700">No content available for export.</p>
        )}

        {message?.imageUrl && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 break-inside-avoid">
            <img
              src={message.imageUrl}
              alt="Exported response"
              className="rounded-lg max-w-full h-auto mx-auto"
              crossOrigin="anonymous"
            />
          </div>
        )}

        {message?.videoUrl && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600 break-inside-avoid">
            Video content is attached in the chat response and is not embedded in this PDF export.
          </div>
        )}

        {message?.audioUrl && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600 break-inside-avoid">
            Audio content is attached in the chat response and is not embedded in this PDF export.
          </div>
        )}
      </div>
    </div>
  );
};

const waitForPdfRender = async (element) => {
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  await new Promise((resolve) => setTimeout(resolve, 500));

  const charts = Array.from(element.querySelectorAll('canvas'));
  for (const canvas of charts) {
    await new Promise((resolve) => {
      const checkCanvas = () => {
        if (canvas.width > 0 && canvas.height > 0) {
          resolve();
        } else {
          requestAnimationFrame(checkCanvas);
        }
      };
      checkCanvas();
    });
  }

  const images = Array.from(element.querySelectorAll('img'));
  await Promise.all(
    images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
    })
  );

  await new Promise((resolve) => setTimeout(resolve, 300));
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
};

const Dashboard = ({ user, onLogout }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [activeNav, setActiveNav] = useState("Home");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [reportModal, setReportModal] = useState({ open: false, loading: false, result: null, topic: "" });
  const [showSchedulePostModal, setShowSchedulePostModal] = useState(false);
  const [schedulePostData, setSchedulePostData] = useState({ mediaUrl: null, caption: "" });
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMessageText, setShareMessageText] = useState("");
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const shouldScrollToBottomRef = useRef(true);
  const [connectors, setConnectors] = useState([]);
  const [typingMessages, setTypingMessages] = useState(new Set());
  const [conversationTitle, setConversationTitle] =
    useState("Your Conversation");
  const [titleGenerated, setTitleGenerated] = useState(false);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [showAskQuestionModal, setShowAskQuestionModal] = useState(false);
  const [askQuestionPrefill, setAskQuestionPrefill] = useState("");
  const [chatHistoryRefreshKey, setChatHistoryRefreshKey] = useState(0);
  const [showQuestionSuccessModal, setShowQuestionSuccessModal] = useState(false);
  const [targetBotMessage, setTargetBotMessage] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const handleScrollToMessage = (messageId) => {
    const element = document.getElementById(`message-item-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(messageId);
      setTimeout(() => {
        setHighlightedMessageId(null);
      }, 2500); // Remove highlight after 2.5 seconds
    }
  };

  // ── Social Post Generation State (new inline flow) ────────────────────────
  // selectedPlatforms: array of connector IDs the user has toggled on
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  // postMediaType: 'image' | 'video'  (only shown when ≥1 platform selected)
  const [postMediaType, setPostMediaType] = useState('image');
  // isGeneratingPost: true while the n8n webhook call is in-flight
  const [isGeneratingPost, setIsGeneratingPost] = useState(false);
  // businessLogo: base64 data URL stored in localStorage
  const [businessLogo, setBusinessLogo] = useState(() => {
    try { return localStorage.getItem('infinium_business_logo') || null; } catch { return null; }
  });

  const handleLogoUpload = (file) => {
    if (!file) return;
    if (businessLogo) {
      const confirmed = window.confirm(
        'A business logo is already saved. Uploading a new one will replace it. Continue?'
      );
      if (!confirmed) return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      try { localStorage.setItem('infinium_business_logo', dataUrl); } catch {}
      setBusinessLogo(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoRemove = () => {
    try { localStorage.removeItem('infinium_business_logo'); } catch {}
    setBusinessLogo(null);
  };

  // Platform icon + colour lookup (keyed by connector.platform string from Postiz)
  const PLATFORM_META_MAP = {
    facebook:  { icon: FaFacebook,  color: '#1877F2', label: 'Facebook'  },
    linkedin:  { icon: FaLinkedin,  color: '#0A66C2', label: 'LinkedIn'  },
    twitter:   { icon: FaXTwitter,  color: '#1DA1F2', label: 'Twitter'   },
    x:         { icon: FaXTwitter,  color: '#1DA1F2', label: 'X'         },
    instagram: { icon: FaInstagram, color: '#E4405F', label: 'Instagram' },
    pinterest: { icon: FaPinterest, color: '#E60023', label: 'Pinterest' },
  };

  /** Toggle a connector platform on/off (CMO only) */
  const togglePostPlatform = (connectorId) => {
    setSelectedPlatforms(prev =>
      prev.includes(connectorId)
        ? prev.filter(id => id !== connectorId)
        : [...prev, connectorId]
    );
  };

  /** Derive the list of connected, usable connectors for the input bar */
  const availableConnectors = connectors.filter(
    c => !c.disabled && !c.refreshNeeded && !c.inBetweenSteps
  );

  // Brand Kit state — persisted in localStorage
  const [brands, setBrands] = useState(loadBrands);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const selectedBrand = brands.find((b) => b.id === selectedBrandId) || null;

  const syncBrands = (updated) => setBrands(updated || loadBrands());

  // Extra messages injected locally (e.g., post_preview cards from n8n)
  const [extraMessages, setExtraMessages] = useState(() => {
    try {
      const key = `postPreviews_${WEBCHAT_CONFIG.clientName}`;
      const saved = sessionStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  // selectedPostPlatforms used by handleScheduleGeneratedPost → SchedulePostModal
  const [selectedPostPlatforms, setSelectedPostPlatforms] = useState([]);
  // Per-card inline schedule state: { [messageId]: { open, date, time, scheduling, scheduled } }
  const [inlineSchedule, setInlineSchedule] = useState({});

  // ── MARKETING STRATEGY STATE ────────────────────────────────────────────────
  // marketingForms: { [messageId]: { open: bool, submitting: bool, submitted: bool } }
  const [marketingForms, setMarketingForms] = useState({});
  // socialPosts: { [messageId]: { posts: [], pendingDays: [], generating: bool } }
  const [socialPosts, setSocialPosts] = useState({});

  // Pinterest boards for generated-post cards (chat posts + marketing strategy posts).
  // pinterestBoards: { [integrationId]: [{ id, name }] } — absent while loading
  // pinterestBoardIds: { [integrationId]: boardId } — one choice per Pinterest account, shared by its cards
  const [pinterestBoards, setPinterestBoards] = useState({});
  const [pinterestBoardIds, setPinterestBoardIds] = useState({});
  const pinterestBoardsRequestedRef = useRef(new Set());

  // ── NEW: MARKETING STRATEGY V2 STATE ──────────────────────────────────────
  // strategyForms: { [messageId]: { open, submitting, submitted } }
  const [strategyForms, setStrategyForms] = useState({});
  // marketingStrategies: { [messageId]: { strategy, open, editing, approved, submitting } }
  const [marketingStrategies, setMarketingStrategies] = useState({});
  // channelSelection: { [messageId]: { open, selectedChannels } }
  const [channelSelection, setChannelSelection] = useState({});
  // offlineForms: { [messageId]: { open, submitting } }
  const [offlineForms, setOfflineForms] = useState({});
  // offlineMarketing: { [messageId]: { materials, pendingTypes: string[], open, submitting, approved } }
  const [offlineMarketing, setOfflineMarketing] = useState({});
  // adsForms: { [messageId]: { open, submitting } }
  const [adsForms, setAdsForms] = useState({});
  // onlineAds: { [messageId]: { campaigns, open, submitting, approved } }
  const [onlineAds, setOnlineAds] = useState({});
  // campaignStatus: { [messageId]: { strategyApproved, offline, onlineAds, socialMedia } }
  const [campaignStatus, setCampaignStatus] = useState({});

  // Persist post_preview cards across page refreshes (session-scoped)
  useEffect(() => {
    try {
      const key = `postPreviews_${WEBCHAT_CONFIG.clientName}`;
      // Strip large mediaUrl blobs before saving to keep sessionStorage small
      const slim = extraMessages.map(m => ({
        ...m,
        mediaUrl: m.mediaUrl?.startsWith('data:') ? null : m.mediaUrl,
      }));
      sessionStorage.setItem(key, JSON.stringify(slim));
    } catch { /* quota exceeded — silently ignore */ }
  }, [extraMessages]);

  // Token management state
  // Check both direct user.currentPlan and nested user.customer.currentPlan
  const getPlan = (u) => u?.currentPlan || u?.display_name || u?.customer?.currentPlan || u?.customer?.display_name || "free_plan";
  const [currentPlan, setCurrentPlan] = useState(getPlan(user));

  const [remainingTokens, setRemainingTokens] = useState(10); // Default to low value until loaded
  const [totalTokens, setTotalTokens] = useState(10);
  const [activePlanVersionId, setActivePlanVersionId] = useState(null);

  const fetchActiveSubscription = React.useCallback(async () => {
    const token = user?.token || user?.jwt || user?.userToken;
    if (!token) return;

    // Helper to calculate total tokens based on plan name
    const getTotalTokensForPlan = (planName) => {
      const planStr = String(planName).toLowerCase();
      if (planStr.includes("market") || planStr.includes("researcher")) {
        return 100;
      } else if (planStr.includes("cmo") || planStr.includes("service")) {
        return 1000;
      }
      return 10;
    };

    try {
      const result = await customerAPI.getActiveSubscription(token);
      if (result.success && result.data && result.data.data) {
        const subData = result.data.data;
        // active sub data has 'plan_versions', containing display_name and credits
        const planVersion = subData.plan_versions;

        // Update Plan — if API can't resolve a real plan, fall back to user's DB record
        let plan = planVersion?.display_name || planVersion?.role || subData.plan_name;
        if (!plan || plan === "free_plan") {
          plan = getPlan(user) || "free_plan";
        }
        setCurrentPlan(plan);

        let calculatedTotal = 10;
        if (planVersion && planVersion.credits !== undefined) {
          calculatedTotal = planVersion.credits;
        } else {
          calculatedTotal = getTotalTokensForPlan(plan);
        }
        setTotalTokens(calculatedTotal);

        // Let's update Role too if provided
        if (planVersion && planVersion.role) {
          setActiveRole(planVersion.role);
        }

        if (subData.plan_version_id) {
          setActivePlanVersionId(subData.plan_version_id);
        }

        console.log("Updated Plan from Active Subscription API:", { plan, calculatedTotal });
      } else {
        // Fallback to user's DB plan if no active subscription record
        const fallbackPlan = getPlan(user) || "free_plan";
        setCurrentPlan(fallbackPlan);
        setTotalTokens(getTotalTokensForPlan(fallbackPlan));
        setActivePlanVersionId(null);
      }
    } catch (error) {
      console.error("Error fetching active subscription:", error);
      // On API error, also fall back to user's DB plan rather than leaving stale state
      const fallbackPlan = getPlan(user) || "free_plan";
      setCurrentPlan(fallbackPlan);
      setTotalTokens(getTotalTokensForPlan(fallbackPlan));
    }
  }, [user]);

  useEffect(() => {
    fetchActiveSubscription();
  }, [fetchActiveSubscription]);

  // Check credits on page load/refresh
  const fetchCreditsOnLoad = React.useCallback(async () => {
    const token = user?.token || user?.jwt || user?.userToken;
    if (!token) return;

    // The /customer/credits endpoint expects the external CHAT user_id
    // (assigned via /customer/assign-user), not the companyCustomerId (CUST...).
    // The chat user ID is stored in the webchat session in localStorage.
    let userId = null;
    try {
      const chatSession = localStorage.getItem(WEBCHAT_CONFIG.clientName);
      if (chatSession) {
        userId = JSON.parse(chatSession).userId;
      }
    } catch (error) {
      console.error("Error reading chat session for credits userId:", error);
    }

    if (!userId) {
      console.warn("Cannot check credits: chat userId not found in session");
      return;
    }

    if (!userId) {
      console.warn("Cannot check credits: userId not found");
      return;
    }

    try {
      console.log("💰 Fetching credits on page load for user:", userId);
      const result = await customerAPI.getCredits(token, userId);

      if (result.success && result.data) {
        // Handle nested data structure: "data": { "data": { "creditsRemaining": ... } }
        const creditsData = result.data.data || result.data;
        const credits = creditsData.creditsRemaining !== undefined
          ? creditsData.creditsRemaining
          : creditsData.credits;

        if (credits !== undefined) {
          setRemainingTokens(credits);
          console.log("✅ Credits loaded on refresh:", credits);

          // Check if credits are zero or negative
          if (credits <= 0) {
            console.warn("⚠️ Insufficient credits detected on load:", credits);
            // The insufficientCredits state in useWebChat will be updated when user tries to send a message
            // But we can show a toast immediately
            // The insufficientCredits state in useWebChat will be updated when user tries to send a message
            // But we can show a toast immediately
            // Handled by insufficientCredits effect
          }
        }
      }
    } catch (error) {
      console.error("Error fetching credits on load:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchCreditsOnLoad();
  }, [fetchCreditsOnLoad]);

// Fetch Postiz connectors on mount and store in sessionStorage
const fetchConnectorsForModal = React.useCallback(async () => {
  let postizToken = sessionStorage.getItem("postiz_token");

  // If no token yet, sync user with Postiz first
  if (!postizToken && user?.email) {
    try {
      const externalId = user.id || user.userId;
      const statusResult = await postizAPI.checkStatus(externalId, user.email);
      if (statusResult?.userExists && statusResult?.token) {
        postizToken = statusResult.token;
        sessionStorage.setItem("postiz_token", postizToken);
        if (statusResult.postizUserId) sessionStorage.setItem("postiz_user_id", statusResult.postizUserId);
        if (statusResult.organizationId) sessionStorage.setItem("postiz_org_id", statusResult.organizationId);
      } else {
        const syncResult = await postizAPI.syncUser({
          email: user.email,
          name: user.name,
          externalUserId: externalId,
        });
        if (syncResult.success && syncResult.data?.token) {
          postizToken = syncResult.data.token;
          sessionStorage.setItem("postiz_token", postizToken);
          sessionStorage.setItem("postiz_user_id", syncResult.data.postizUserId);
          sessionStorage.setItem("postiz_org_id", syncResult.data.organizationId);
        }
      }
    } catch (err) {
      console.error("Error syncing Postiz user in Dashboard:", err);
    }
  }

  if (!postizToken) return;

  try {
    const result = await postizAPI.getConnectors(postizToken);
    const connectorList = result.data || [];
    setConnectors(connectorList);
    sessionStorage.setItem('postiz_connectors', JSON.stringify(connectorList));
  } catch (error) {
    console.error("Error fetching connectors for modal:", error);
  }
}, [user]);

useEffect(() => {
  fetchConnectorsForModal();
  // Refresh connectors every 30 seconds
  const interval = setInterval(fetchConnectorsForModal, 30000);
  return () => clearInterval(interval);
}, [fetchConnectorsForModal]);
  // Notifications API Logic
  const fetchNotifications = React.useCallback(async () => {
    const token = user?.token || user?.jwt || user?.userToken;
    if (!token) return;

    try {
      const response = await fetch('https://customer.versalence.online/admin/getCreditsNotifications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'Success' && result.data) {
          setNotifications(result.data);
          const unread = result.data.filter(n => !n.is_seen).length;
          setUnreadCount(unread);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkNotificationsSeen = async () => {
    const token = user?.token || user?.jwt || user?.userToken;
    if (!token || unreadCount === 0) return;

    try {
      const response = await fetch('https://customer.versalence.online/admin/markSeenNotifications', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'Success') {
          // Optimistically update local state
          setNotifications(prev => prev.map(n => ({ ...n, is_seen: true })));
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error("Error marking notifications seen:", error);
    }
  };

  // Restore chat from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const chatId = params.get("chatId");

    if (chatId) {
      console.log("🔄 Restoring chat from URL:", chatId);

      // Try to find title in local storage
      const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
      const storedHistory = localStorage.getItem(historyKey);
      let title = "Restored Conversation";

      if (storedHistory) {
        try {
          const history = JSON.parse(storedHistory);
          const chat = history.find(c => c.conversationKey === chatId || c.id === chatId);
          if (chat && chat.title) {
            title = chat.title;
          }
        } catch (e) {
          console.error("Error parsing history for restoration:", e);
        }
      }

      // Restore UI State
      setIsHistoryView(true);
      setActiveNav("Home");
      setConversationTitle(title);

      // Load messages
      loadMessagesFromHistory(chatId).then(res => {
        if (res.success) {
          console.log("✅ Restored chat messages from URL ID");
          scrollToTop();
        }
      });
    }
  }, []);

  // Keep existing useEffect for user prop changes as fallback/immediate update if user prop has info
  useEffect(() => {
    const plan = getPlan(user);
    if (plan !== currentPlan) {
      setCurrentPlan(plan);
    }
  }, [user]);

  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState("idle"); // idle, processing, success, error
  const [verificationMessage, setVerificationMessage] = useState("");

  // Handle PayPal Return
  useEffect(() => {
    const handlePayPalReturn = async () => {
      const params = new URLSearchParams(window.location.search);
      const paypalStatus = params.get("paypal");
      const orderId = params.get("token"); // PayPal returns order ID as 'token'
      const payerId = params.get("PayerID");

      // Early exit if no PayPal status provided 
      if (!paypalStatus) return;

      console.log("🔍 PayPal Params:", { paypalStatus, orderId, payerId });

      // Clean URL helper
      const cleanUrl = () => {
        window.history.replaceState({}, "", "/dashboard");
      };

      // ❌ PAYMENT CANCELLED
      if (paypalStatus === "cancel") {
        console.log("❌ PayPal payment cancelled");
        clearPendingPayment();
        toast.warning("Payment was cancelled", { toastId: "paypal-cancelled" });
        cleanUrl();
        return;
      }

      // ✅ PAYMENT SUCCESS
      if (paypalStatus === "success" && orderId) {
        // Try to get token from user prop OR localStorage
        let token = user?.token || user?.jwt || user?.userToken;
        if (!token) {
          token = localStorage.getItem("token") || localStorage.getItem("jwt") || localStorage.getItem("userToken");
        }

        if (!token) {
          console.warn("⏳ User token missing, waiting for auth...");
          // We return here. If 'user' changes, this key-effect will re-run and find the token.
          return;
        }

        // Prevent duplicate verifications
        if (window.paypalVerifying === orderId) {
          console.log("🔄 Verification already in progress...");
          return;
        }

        if (!hasMatchingPendingPayment({ gateway: "paypal", orderId })) {
          console.warn("⚠️ Ignoring PayPal verification without matching pending order", orderId);
          setVerificationStatus("error");
          setVerificationMessage("Payment verification could not be confirmed for this session. Please retry from the payment flow.");
          cleanUrl();
          return;
        }

        window.paypalVerifying = orderId;

        // Start UI
        setVerifyingPayment(true);
        setVerificationStatus("processing");
        setVerificationMessage("Verifying your payment with PayPal...");

        console.log("🚀 Verifying payment with order/token:", orderId);

        try {
          const verifyResult = await customerAPI.verifyPayment(token, {
            gateway: "paypal",
            orderId: orderId
          });

          if (verifyResult.success) {
            console.log("✅ Verified:", verifyResult.data);
            clearPendingPayment();
            setVerificationStatus("success");
            setVerificationMessage("Payment successful! Your plan has been updated.");

            // Update local state if needed
            if (verifyResult.data.totalCredits) {
              setRemainingTokens(verifyResult.data.totalCredits);
            }
            await fetchActiveSubscription(); // Refresh profile

            cleanUrl();

            // Close modal after delay
            setTimeout(() => {
              setVerifyingPayment(false);
              setVerificationStatus("idle");
            }, 3000);

          } else {
            console.error("❌ Verification Failed:", verifyResult);
            setVerificationStatus("error");
            setVerificationMessage("Verification failed: " + (verifyResult.error?.message || verifyResult.error || "Unknown error"));
          }
        } catch (err) {
          console.error("❌ Verification Exception:", err);
          setVerificationStatus("error");
          setVerificationMessage("An error occurred during verification. Please contact support.");
        } finally {
          delete window.paypalVerifying;
        }
      }
    };

    handlePayPalReturn();
  }, [user]); // Re-run when user updates (authentication loads)

  // Feedback State
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSuccessModalOpen, setFeedbackSuccessModalOpen] = useState(false);
  const [feedbackMessageId, setFeedbackMessageId] = useState(null);

  const handleFeedbackSubmit = async (feedbackText, specificMsgId = null) => {
    // Determine which message ID to use (passed directly or from state)
    const targetMsgId = specificMsgId || feedbackMessageId;

    // Find the message content
    const targetMessage = chatMessages.find(msg => msg.id === targetMsgId);
    const feedbackForContent = targetMessage ? targetMessage.content : "agent";

    try {
      const response = await fetch('https://chat.versalence.info/interactions/infinium/postFeedback', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CHAT_AUTH_TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          uuid: WEBCHAT_CONFIG.uuid,
          userId: userSession?.userId || user?.userId || "anonymous",
          feedback: feedbackText,
          feedBackFor: feedbackForContent,
          source: "webchat",
          userEmail: user?.email || user?.companyCustomerPriEmail || "anonymous@example.com",
          userName: user?.name || user?.firstName || "Anonymous",
          conversationId: userSession?.vcxConversationId || userSession?.conversationId || "",
          messageId: targetMsgId || "",
          withChat: true
        })
      });

      if (response.ok) {
        setFeedbackSuccessModalOpen(true);
      } else {
        console.error("Feedback API error:", response.status);
        toast.error("Failed to submit feedback. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("An error occurred while submitting feedback.");
    }
  };

  const onThumbsUp = (msgId) => {
    // For thumbs up, we just send a generic positive feedback
    // Pass msgId directly so we don't rely on async state update for immediate submit
    setFeedbackMessageId(msgId);
    handleFeedbackSubmit("Positive Feedback (Thumbs Up)", msgId);
  };

  const onThumbsDown = (msgId) => {
    setFeedbackMessageId(msgId);
    setFeedbackModalOpen(true);
  };

  const handleRetryLastResponse = () => {
  if (chatMessages.length === 0) return;

  const lastMessage = chatMessages[chatMessages.length - 1];

  if (lastMessage.type === "user") {
    // Stop any in-flight response first, then resend
    stopResponse().then(() => {
      editAndResend(lastMessage.id, lastMessage.content);
    });
    return;
  }

  // Last message is an assistant reply — regenerate it
  regenerateResponse(lastMessage.id);
};

  // File Upload State
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  // Post media override state — { [msgId]: { items: [{url,type},...], activeIndex: number } }
  const [postMediaOverrides, setPostMediaOverrides] = useState({});
  const postMediaInputRef = useRef(null);
  const postMediaTargetId = useRef(null);

  const handleDiscardPostMedia = (msgId) => {
    setPostMediaOverrides(prev => {
      const cur = prev[msgId];
      const items = cur?.items || [];
      const activeIndex = cur?.activeIndex ?? 0;
      const newItems = items.filter((_, i) => i !== activeIndex);
      return { ...prev, [msgId]: { items: newItems, activeIndex: Math.min(activeIndex, Math.max(0, newItems.length - 1)) } };
    });
  };

  const handleAddPostMedia = (msgId) => {
    postMediaTargetId.current = msgId;
    // Store the original media so we can seed it if no override exists yet
    if (!postMediaOverrides[msgId]) {
      const postMsg = extraMessages.find(m => m.id === msgId);
      if (postMsg?.mediaUrl) {
        setPostMediaOverrides(prev => {
          if (prev[msgId] !== undefined) return prev; // already set, don't overwrite
          return { ...prev, [msgId]: { items: [{ url: postMsg.mediaUrl, type: postMsg.mediaType || 'image' }], activeIndex: 0 } };
        });
      }
    }
    postMediaInputRef.current?.click();
  };

  const handlePostMediaFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !postMediaTargetId.current) return;
    const id = postMediaTargetId.current;
    setPostMediaOverrides(prev => {
      const cur = prev[id];
      const existing = cur?.items || [];
      const newItems = [
        ...existing,
        ...files.map(f => ({ url: URL.createObjectURL(f), type: f.type.startsWith('video') ? 'video' : 'image' })),
      ];
      return { ...prev, [id]: { items: newItems, activeIndex: existing.length } };
    });
    e.target.value = null;
  };

  const handleCarouselNav = (msgId, delta) => {
    setPostMediaOverrides(prev => {
      const cur = prev[msgId];
      if (!cur) return prev;
      const len = cur.items.length;
      if (len === 0) return prev;
      const next = (cur.activeIndex + delta + len) % len;
      return { ...prev, [msgId]: { ...cur, activeIndex: next } };
    });
  };

  const getEffectivePostMediaAll = (postMsg) => {
    const override = postMediaOverrides[postMsg.id];
    const items = override !== undefined
      ? (override.items || [])
      : (postMsg.mediaUrl ? [{ url: postMsg.mediaUrl, type: postMsg.mediaType }] : []);
    // Postiz's Pinterest provider uploads the first item as the video and uses the image as its cover
    return postMsg.platform === 'pinterest' ? putVideoFirst(items) : items;
  };

  // Uploads all media items for a post, returns Postiz images array
  const uploadMediaItems = async (postizToken, mediaItems) => {
    const images = [];
    for (const { url, type } of mediaItems) {
      if (!url) continue;
      if (type === 'video') {
        toast.info('Uploading video to Postiz…', { toastId: 'video-upload', autoClose: false });
        try {
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`Video fetch failed (${resp.status})`);
          const blob = await resp.blob();
          const mime = blob.type || 'video/mp4';
          const file = new File([blob], `post-video.${mime.split('/')[1] || 'mp4'}`, { type: mime });
          const r = await postizAPI.uploadMedia(postizToken, file);
          toast.dismiss('video-upload');
          if (!r.success) throw new Error('Video upload failed: ' + (r.error || 'unknown'));
          images.push({ id: r.id || r.url, path: r.url });
        } catch (err) {
          toast.dismiss('video-upload');
          console.warn('Video upload failed, using URL directly:', err.message);
          images.push({ id: url, path: url });
        }
      } else if (url.startsWith('data:')) {
        const commaIdx = url.indexOf(',');
        if (commaIdx === -1) continue;
        const mime = url.slice(0, commaIdx).match(/:(.*?);/)?.[1] || 'image/jpeg';
        const ext = mime.split('/')[1]?.split('+')[0] || 'jpg';
        const b64 = url.slice(commaIdx + 1);
        const bytes = atob(b64);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const file = new File([new Blob([arr], { type: mime })], `post-image.${ext}`, { type: mime });
        const r = await postizAPI.uploadMedia(postizToken, file);
        if (!r.success) throw new Error('Image upload failed: ' + (r.error || 'unknown'));
        images.push({ id: r.id || r.url, path: r.url });
      } else {
        try {
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`Image fetch failed (${resp.status})`);
          const blob = await resp.blob();
          const mime = blob.type || 'image/jpeg';
          const file = new File([blob], `post-image.${mime.split('/')[1] || 'jpg'}`, { type: mime });
          const r = await postizAPI.uploadMedia(postizToken, file);
          if (!r.success) throw new Error(r.error || 'unknown');
          images.push({ id: r.id || r.url, path: r.url });
        } catch (err) {
          console.warn('Image fetch failed, passing URL directly:', err.message);
          images.push({ id: url, path: url });
        }
      }
    }
    return images;
  };

  // Get user role from user object or token
  const initialUserRole = useMemo(() => {
    if (user?.role) return user.role;
    const token = user?.token || user?.jwt;
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded.role;
    } catch (error) {
      console.error("Error decoding token for role:", error);
      return null;
    }
  }, [user]);

  const [activeRole, setActiveRole] = useState(initialUserRole); // Local state for role to support immediate updates

  useEffect(() => {
    setActiveRole(initialUserRole);
  }, [initialUserRole]);

  // WebChat integration
  const {
    messages: chatMessages,
    isTyping: isChatTyping,
    isConnected: isChatConnected,
    isHistoryLoading,
    sendMessage: sendChatMessage,
    regenerateResponse,
    editAndResend,
    sendQuickReply,
    submitForm,
    resetChat,
    deleteBotpressMessages,
    config: chatConfig,
    userSession,
    loadMessagesFromHistory,
    insufficientCredits,
    planExpired,
    setPlanExpired,
    notification, // Destructure notification
    responseDelayed,
    dismissResponseDelayed,
    registerPostPreviewHandler,
    pollForPostPreview,
    stopResponse,
    canStop,
  } = useWebChat(user);

  const resetComposerState = () => {
    setIsGeneratingPost(false);
    setIsUploading(false);
    setSelectedPlatforms([]);
    setPostMediaType("image");
  };

  const navItems = [
    { name: "Home", icon: "home", label: "Home" },
    { name: "Discover", icon: "discover", label: "Discover" },
    { name: "History", icon: "history", label: "History" },
    { name: "Questions", icon: "question", label: "My Questions" },
    { name: "Performance Marketing", icon: "bar-chart", label: "Performance Marketing" },
    { name: "Brands", icon: "brands", label: "Brand Kit" },
  ];


  // Clear planExpired lock when payment succeeds
  useEffect(() => {
    if (verificationStatus === "success" && setPlanExpired) {
      setPlanExpired(false);
    }
  }, [verificationStatus, setPlanExpired]);

  useEffect(() => {
    console.log("Dashboard: isChatTyping changed to:", isChatTyping, "Messages count:", chatMessages.length);
    if (shouldScrollToBottomRef.current || isChatTyping || isNearBottom()) {
      scrollToBottom();
    }
    if (!isChatTyping) {
      shouldScrollToBottomRef.current = false;
    }
  }, [chatMessages, isChatTyping]);

  // Refresh credit balance whenever the AI finishes responding
  const prevTypingRef = useRef(false);
  useEffect(() => {
    if (prevTypingRef.current && !isChatTyping) {
      // Typing just stopped — re-fetch credits to reflect server-side deduction
      fetchCreditsOnLoad();

      // Start typewriter animation only for the live response that just completed.
      // History-loaded messages never reach here because isChatTyping is already false.
      const lastMessage = chatMessages[chatMessages.length - 1];
      if (lastMessage && lastMessage.type === "assistant" && lastMessage.content) {
        setTypingMessages((prev) => new Set(prev).add(lastMessage.id));
      }
    }
    prevTypingRef.current = isChatTyping;
  }, [isChatTyping, fetchCreditsOnLoad, chatMessages]);

  // Effect to handle insufficient credits toast
  useEffect(() => {
    if (insufficientCredits) {
      const toastContent = (
        <div
          key={isDarkMode ? "dark" : "light"}
          className={`relative overflow-hidden rounded-2xl p-5 shadow-2xl backdrop-blur-xl border w-full max-w-md ${isDarkMode ? "bg-gray-900/95 border-white/10" : "bg-white/95 border-gray-200"
            }`}
        >
          {/* Glow Effect */}
          <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-red-500/10 blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-blue-500/10 blur-3xl pointer-events-none"></div>

          <button
            onClick={() => toast.dismiss("insufficient-credits-active")}
            className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors z-20 ${isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-white/10"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <div className="relative z-10 flex flex-col gap-3">
            <div className="flex items-start gap-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-sm ${isDarkMode ? "bg-gradient-to-br from-red-500/20 to-red-900/20 border-red-500/30" : "bg-red-50 border-red-100"
                }`}>
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1 space-y-0.5">
                <h3 className={`font-bold text-base tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  Limit Reached
                </h3>
                <p className={`text-sm leading-relaxed font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  You've used all your credits. Unlock unlimited potential with Pro.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowPaymentModal(true)}
              className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-red-600 to-rose-600 p-[1px] shadow-sm transition-all hover:shadow-red-500/25 active:scale-[0.98]"
            >
              <div className={`relative flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all group-hover:bg-transparent ${isDarkMode ? "bg-gray-900/50 text-white" : "bg-white text-gray-900 group-hover:text-white"
                }`}>
                <span>Upgrade Plan</span>
                <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      );

      const toastOptions = {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        toastId: "insufficient-credits-active",
        // Reset default styles
        className: "!bg-transparent !p-0 !shadow-none !border-0 !rounded-none !min-w-0 !w-auto",
        bodyClassName: "!bg-transparent !p-0 !m-0 !border-0",
        icon: false,
        closeButton: false,
      };

      if (toast.isActive("insufficient-credits-active")) {
        toast.update("insufficient-credits-active", {
          render: toastContent,
          ...toastOptions // Re-apply options to ensure consistency
        });
      } else {
        toast(toastContent, toastOptions);
      }
    } else {
      toast.dismiss("insufficient-credits-active");
    }
  }, [insufficientCredits, isDarkMode]);

  // Handle direct plan renewal
  const handleRenewPlan = async () => {
    try {
      const token = user?.token || user?.jwt || user?.userToken;
      if (!token) {
        toast.error("Please login to proceed with payment.");
        return;
      }

      const renewToastId = toast.loading("Initializing renewal...", {
        position: "top-right",
      });

      // 1. Get active subscription to get planVersionId
      const activeRes = await customerAPI.getActiveSubscription(token);
      const planVersionId = activeRes.data?.plan_version_id || activeRes.data?.data?.plan_version_id;
      if (!activeRes.success || !planVersionId) {
        toast.dismiss(renewToastId);
        toast.info("No active subscription found. Please choose a plan to subscribe.", { autoClose: 4000 });
        setShowUpgradeModal(true);
        return;
      }

      // 2. Renew Payment (calling POST to /customer/payments/renew)
      const renewRes = await customerAPI.renewSubscription(token, planVersionId, "razorpay", "INR");
      if (!renewRes.success) {
        toast.update(renewToastId, { render: renewRes.error || "Failed to create renewal order", type: "error", isLoading: false, autoClose: 3000 });
        return;
      }

      toast.dismiss(renewToastId);

      const orderData = renewRes.data;
      if (orderData.order) {
        const { id: order_id, amount, currency } = orderData.order;
        storePendingPayment({ gateway: "razorpay", orderId: order_id, source: "dashboard-renewal" });

        // Load Razorpay SDK
        if (!window.Razorpay) {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          await new Promise((resolve) => {
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
          });
        }

        const options = {
          key: api.razorpayKey,
          amount: amount,
          currency: currency || "INR",
          name: "Infinium Intelligence",
          description: "Renew Subscription",
          order_id: order_id,
          handler: async function (response) {
            const verifyToast = toast.loading("Verifying Payment...", {
              position: "top-right"
            });
            try {
              if (!hasMatchingPendingPayment({ gateway: "razorpay", orderId: response.razorpay_order_id })) {
                toast.update(verifyToast, { render: "Payment verification rejected for this session.", type: "error", isLoading: false, autoClose: 3000 });
                return;
              }

              const verifyResult = await customerAPI.verifyPayment(token, {
                gateway: "razorpay",
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });

              if (verifyResult.success) {
                clearPendingPayment();
                toast.update(verifyToast, { render: "Payment Successful!", type: "success", isLoading: false, autoClose: 3000 });
                // clear plan expired status and refresh profiles immediately or just reload
                if (setPlanExpired) setPlanExpired(false);
                setTimeout(() => window.location.reload(), 2000);
              } else {
                toast.update(verifyToast, { render: "Payment verification failed", type: "error", isLoading: false, autoClose: 3000 });
              }
            } catch (err) {
              console.error("Verification error", err);
              toast.update(verifyToast, { render: "Payment verification failed during request.", type: "error", isLoading: false, autoClose: 3000 });
            }
          },
          prefill: {
            name: user?.name || user?.firstName || "",
            email: user?.email || "",
            contact: user?.phone || user?.contact || ""
          },
          theme: {
            color: "#2563eb"
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
          toast.error(response.error.description || "Payment failed");
        });
        rzp.open();
      } else {
        toast.error("Invalid response from renewal API");
      }
    } catch (err) {
      console.error("Renewal Error:", err);
      toast.error("An error occurred during renewal.");
    }
  };

  // Effect to handle Plan Expired toast
  useEffect(() => {
    if (planExpired) {
      const toastContent = (
        <div
          key={isDarkMode ? "dark" : "light"}
          className={`relative overflow-hidden rounded-2xl p-5 shadow-2xl backdrop-blur-xl border w-full max-w-md ${isDarkMode ? "bg-gray-900/95 border-white/10" : "bg-white/95 border-gray-200"
            }`}
        >
          {/* Glow Effect */}
          <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-orange-500/10 blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-yellow-500/10 blur-3xl pointer-events-none"></div>

          <button
            onClick={() => toast.dismiss("plan-expired-active")}
            className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors z-20 ${isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-white/10"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>

          <div className="relative z-10 flex flex-col gap-3">
            <div className="flex items-start gap-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-sm ${isDarkMode ? "bg-gradient-to-br from-orange-500/20 to-orange-900/20 border-orange-500/30" : "bg-orange-50 border-orange-100"
                }`}>
                <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1 space-y-0.5 mt-0.5">
                <h3 className={`font-bold text-base tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  Plan Expired
                </h3>
                <p className={`text-sm leading-relaxed font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Your plan has expired. Please renew it to continue using the service.
                </p>
              </div>
            </div>

            <button
              onClick={handleRenewPlan}
              className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 to-yellow-600 p-[1px] shadow-sm transition-all hover:shadow-orange-500/25 active:scale-[0.98] mt-2"
            >
              <div className={`relative flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all group-hover:bg-transparent ${isDarkMode ? "bg-gray-900/50 text-white" : "bg-white text-gray-900 group-hover:text-white"
                }`}>
                <span>Renew Plan</span>
                <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      );

      const toastOptions = {
        position: "top-right",
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        toastId: "plan-expired-active",
        className: "!bg-transparent !p-0 !shadow-none !border-0 !rounded-none !min-w-0 !w-auto",
        bodyClassName: "!bg-transparent !p-0 !m-0 !border-0",
        icon: false,
        closeButton: false,
      };

      if (toast.isActive("plan-expired-active")) {
        toast.update("plan-expired-active", {
          render: toastContent,
          ...toastOptions // Re-apply options to ensure consistency
        });
      } else {
        toast(toastContent, toastOptions);
      }
    } else {
      toast.dismiss("plan-expired-active");
    }
  }, [planExpired, isDarkMode]);

  // Effect to handle generic API error notifications
  useEffect(() => {
    if (notification && notification.type === 'error') {
      if (notification.message === "You have insufficient credits to continue chatting") return;
      toast.error(notification.message, {
        toastId: `error-${notification.id}` // Unique ID based on timestamp
      });
    } else if (notification && notification.type === 'success') {
      toast.success(notification.message, {
        toastId: `success-${notification.id}`
      });
    }
  }, [notification]);



  // Generate conversation title
  useEffect(() => {
    const generateTitle = async () => {
      if (
        chatMessages.length > 0 &&
        chatMessages.length % 4 === 0 &&
        !titleGenerated
      ) {
        try {
          // Take only the last 8 messages for context
          const recentMessages = chatMessages.slice(-8);
          const conversationData = recentMessages.map((msg) => ({
            role: msg.type === "user" ? "user" : "assistant",
            message: msg.content,
          }));

          const response = await fetch(
            "https://workflow.igr-consulting.com/webhook/140630b2-e8fa-4dcd-87e9-772f933dcd47",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ conversation: conversationData }),
            }
          );

          if (response.ok) {
            const data = await response.json();
            const generatedTitle = data.output?.[0]?.content?.[0]?.text;
            if (generatedTitle) {
              console.log("✅ Webhook generated title:", generatedTitle);
              setConversationTitle(generatedTitle);
              setTitleGenerated(true);

              // 2️⃣ Mandatory Action: Immediately call Add Conversation Title API
              // USE vcxConversationId for title API as per requirement
              const targetId = userSession?.vcxConversationId || userSession?.conversationId;

              if (targetId) {
                if (!userSession?.vcxConversationId) console.warn("⚠️ Using fallback ID for title add:", targetId);

                await addConversationTitle(
                  targetId,
                  WEBCHAT_CONFIG.uuid,
                  generatedTitle
                );
                // Sync local history with internal ID
                if (userSession?.conversationId) {
                  updateChatHistoryTitle(userSession.conversationId, generatedTitle);
                }
              } else {
                console.warn("⚠️ Cannot add title: active conversationId is missing");
              }
            }
          }
        } catch (error) {
          console.error("Error generating title:", error);
        }
      } else if (chatMessages.length % 4 !== 0) {
        setTitleGenerated(false);
      }
    };

    generateTitle();
  }, [chatMessages, titleGenerated, userSession?.conversationId]);

  // Update heading with first user message text while awaiting the AI-generated title
  useEffect(() => {
    if (!titleGenerated && !isHistoryView) {
      const firstUserMsg = chatMessages.find((m) => m.type === "user");
      if (firstUserMsg) {
        const preview = firstUserMsg.content?.substring(0, 60) || "";
        if (preview) {
          setConversationTitle(preview.length < firstUserMsg.content?.length ? preview + "…" : preview);
        }
      }
    }
  }, [chatMessages, titleGenerated, isHistoryView]);

  // Fetch conversation title on load / reload
  useEffect(() => {
    const fetchTitle = async () => {
      // Use vcxConversationId for title API
      const targetId = userSession?.vcxConversationId || userSession?.conversationId;

      if (targetId && WEBCHAT_CONFIG?.uuid) {
        if (!userSession?.vcxConversationId) console.warn("⚠️ Using fallback ID for title fetch:", targetId);

        try {
          const res = await getConversationTitle(targetId, WEBCHAT_CONFIG.uuid);
          const GENERIC_TITLES = ["Your Conversation", "Conversation", "New Conversation", ""];
          if (res.success && res.data?.conversationName) {
            const fetchedTitle = res.data.conversationName.trim();
            console.log("✅ Fetched conversation title:", fetchedTitle);
            if (!GENERIC_TITLES.includes(fetchedTitle)) {
              setConversationTitle(fetchedTitle);
              setTitleGenerated(true);
              // Sync local history with internal ID only when title is meaningful
              if (userSession?.conversationId) {
                updateChatHistoryTitle(userSession.conversationId, fetchedTitle);
              }
            }
          }
        } catch (error) {
          console.error("Failed to fetch conversation title:", error);
        }
      }
    };

    fetchTitle();
  }, [userSession?.conversationId, userSession?.vcxConversationId]);

  const scrollToBottom = (instant = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (instant) {
      container.scrollTop = container.scrollHeight;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTop = () => {
    messagesContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 120;
  };

  const handleScrollContainer = () => {
    setShowScrollButton(!isNearBottom());
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const message = searchQuery.trim();
    if (!message) return;

    shouldScrollToBottomRef.current = true;

    const isFreshDashboardPrompt = !isHistoryView && chatMessages.length === 0;

    // If the user has selected platforms (CMO only), run post generation instead of normal chat
    if (currentPlan?.includes('CMO') && selectedPlatforms.length > 0) {
      setSearchQuery('');
      if (isFreshDashboardPrompt) {
        await resetChat();
      }
      await handleGeneratePostFromInput(message);
      return;
    }

    setSearchQuery('');
    if (isFreshDashboardPrompt) {
      await resetChat();
    }
    await sendChatMessage(message);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert("File size should be less than 10MB");
      return;
    }

    try {
      setIsUploading(true);

      // Step 1: Get presigned URL
      const key = `uploads/${Date.now()}_${file.name}`;
      const step1Response = await fetch(`${botpress.apiUrl}/files`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${botpress.token}`,
          "x-bot-id": botpress.botId,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          key: key,
          size: file.size,
          accessPolicies: ["public_content"],
          contentType: file.type // Ensure content type is matched for presigned URL
        })
      });

      if (!step1Response.ok) {
        throw new Error("Failed to initiate upload");
      }

      const step1Data = await step1Response.json();

      // Handle potential nested structure (e.g. { file: { uploadUrl: ... } })
      const fileData = step1Data.file || step1Data;

      const uploadUrl = fileData.uploadUrl;
      const publicUrl = fileData.url || fileData.publicUrl;

      if (!uploadUrl) {
        throw new Error("Invalid response from upload API: Missing upload URL");
      }

      // Step 2: Upload file to presigned URL
      const step2Response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type
        },
        body: file
      });

      if (!step2Response.ok) {
        throw new Error("Failed to upload file to S3");
      }

      console.log("File uploaded:", publicUrl);

      // Track File Upload
      const token = user?.token || user?.jwt;
      if (token) {
        await customerAPI.trackFile(token, {
          fileType: file.name.split(".").pop() || "unknown", // Simple extension extraction
          fileUrl: publicUrl,
          title: file.name,
          source: "botpress",
        });
        console.log("File tracked successfully");
      }

      // Send the public URL as a message
      await sendChatMessage(publicUrl, "image"); // Assuming sendChatMessage can take a type or we just send the URL and let the user see it

    } catch (err) {
      console.error("Upload error:", err);
      // alert("Failed to upload file: " + err.message); // Consider using a toast or UI error state instead of alert
    } finally {
      setIsUploading(false);
      e.target.value = null; // Reset input
    }
  };

  const handleGeneratePDF = async (messageId) => {
    const message = chatMessages.find((item) => item.id === messageId);
    if (!message) return;

    let exportHost = null;
    let exportRoot = null;

    try {
      const button = document.getElementById(`pdf-btn-${messageId}`);
      if (button) {
        button.disabled = true;
        button.textContent = "Generating...";
      }

      exportHost = document.createElement('div');
      exportHost.id = `pdf-export-host-${messageId}`;
      exportHost.style.position = 'fixed';
      exportHost.style.left = '-20000px';
      exportHost.style.top = '0';
      exportHost.style.width = '1024px';
      exportHost.style.opacity = '1';
      exportHost.style.pointerEvents = 'none';
      exportHost.style.zIndex = '-1';
      exportHost.style.background = '#ffffff';
      document.body.appendChild(exportHost);

      exportRoot = createRoot(exportHost);
      exportRoot.render(
        <PdfExportMessage
          message={message}
          conversationTitle={conversationTitle}
        />
      );

      await waitForPdfRender(exportHost);

      // Capture full content as single high-quality canvas
      const canvas = await html2canvas(exportHost, {
        scale: 4,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1024,
      });

      const margin = 10;
      const pageWidthMm = 210;
      const printableWidth = pageWidthMm - margin * 2;
      const imageHeightMm = (canvas.height / canvas.width) * printableWidth;
      const pageHeightMm = Math.max(297, imageHeightMm + margin * 2);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidthMm, pageHeightMm]
      });

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(
        imgData,
        'PNG',
        margin,
        margin,
        printableWidth,
        imageHeightMm,
        undefined,
        'NONE'
      );

      pdf.save(`conversation-message-${messageId}.pdf`);

      exportRoot.unmount();
      document.body.removeChild(exportHost);
      exportRoot = null;
      exportHost = null;

      if (button) {
        button.disabled = false;
        button.textContent = "Generate PDF";
      }

    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF");
      if (exportRoot) {
        exportRoot.unmount();
      }
      if (exportHost && document.body.contains(exportHost)) {
        document.body.removeChild(exportHost);
      }
      const button = document.getElementById(`pdf-btn-${messageId}`);
      if (button) {
        button.disabled = false;
        button.textContent = "Generate PDF";
      }
    }
  };

  const handleGeneratePPT = async (messageId) => {
    const message = chatMessages.find((item) => item.id === messageId);
    if (!message) return;

    const messageText = message.isHtml
      ? stripHtmlToText(message.content || "")
      : String(message.content || "");

    const wordCount = String(messageText || "")
      .split(/\s+/)
      .filter(Boolean).length;

    if (wordCount <= 100) {
      toast.info("PPT generation is available for longer responses.");
      return;
    }

    if (!currentPlan?.includes("CMO")) {
      setShowPaymentModal(true);
      return;
    }

    const button = document.getElementById(`ppt-btn-${messageId}`);

    try {
      if (button) {
        button.disabled = true;
        button.textContent = "Generating...";
      }

      const response = await fetch(N8N_PPT_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messageId,
          messageText,
          conversationTitle: conversationTitle || "",
          userId: user?.id || user?.userId || "",
        }),
      });

      if (!response.ok) {
        throw new Error(`Webhook error (${response.status})`);
      }

      // Response is now a binary .pptx file, not JSON
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `presentation-${ts}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      console.error("Error generating PPT:", error);
      toast.error(`Failed to generate PPT: ${msg}`);
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = "Generate PPT";
      }
    }
  };

  const handleDownloadReport = async (messageId) => {
    const message = chatMessages.find((item) => item.id === messageId);
    if (!message) return;

    const messageIndex = chatMessages.findIndex((item) => item.id === messageId);
    const precedingUserMessage = messageIndex > 0
      ? [...chatMessages].slice(0, messageIndex).reverse().find((m) => m.type === "user")
      : null;

    const topic = precedingUserMessage?.content
      ? precedingUserMessage.content.slice(0, 100) + (precedingUserMessage.content.length > 100 ? "..." : "")
      : "your query";

    // Open modal in loading state immediately
    setReportModal({ open: true, loading: true, result: null, topic });

    try {
      const response = await fetch(N8N_REPORT_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: userSession?.userId || "anonymous",
          conversationId: userSession?.vcxConversationId || userSession?.conversationId || "",
          sessionId: `report_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          role: message.type || "assistant",
          userPrompt: precedingUserMessage?.content || "",
          botMessage: message.content || "",
        }),
      });

      if (!response.ok) throw new Error(`Webhook error (${response.status})`);

      const data = await response.json();
      console.log("[ReportModal] raw n8n response:", JSON.stringify(data));

      // Try every plausible shape n8n may return
      let parsed = null;
      const candidates = [
        data?.[0]?.output,
        data?.[0]?.json?.output,
        data?.[0]?.data?.output,
        data?.output,
        data,
      ];
      for (const candidate of candidates) {
        if (!candidate) continue;
        try {
          const obj = typeof candidate === "string" ? JSON.parse(candidate) : candidate;
          if (obj && typeof obj === "object" && ("bestMatchFileLink" in obj || "bestMatchFileName" in obj || "bestMatchFiles" in obj || "confidence" in obj)) {
            parsed = obj;
            break;
          }
        } catch (_) { /* not valid JSON, try next */ }
      }

      console.log("[ReportModal] parsed result:", parsed);
      setReportModal({ open: true, loading: false, result: parsed || { _raw: JSON.stringify(data) }, topic });

    } catch (error) {
      console.error("Error fetching report:", error);
      setReportModal({ open: true, loading: false, result: { error: true }, topic });
    }
  };

  /**
   * Called when the user submits the input with ≥1 platform selected.
   * Sends conversation context + user requirements to n8n, then injects
   * one "post_preview" bot message per platform into the chat.
   *
   * Expected n8n response shape (array):
   * [
   *   {
   *     "platform": "facebook",          // matches connector.platform
   *     "integrationId": "<postiz-id>",  // connector.id from Postiz
   *     "caption": "...",
   *     "hashtags": ["#tag1", "#tag2"],
   *     "mediaUrl": "https://...",       // generated image/video URL (or base64)
   *     "mediaType": "image"             // "image" | "video"
   *   },
   *   ...one object per selected platform
   * ]
   */
  const handleGeneratePostFromInput = async (userRequirements) => {
  if (!userRequirements.trim()) return;
  if (selectedPlatforms.length === 0) return;

  setIsGeneratingPost(true);

  // ── Fetch fresh IDs before firing n8n ────────────────────────────
  let freshVcxConversationId = userSession?.vcxConversationId || '';
  let freshConversationId = userSession?.conversationId || '';

  if (userSession?.userId) {
    try {
      const historyRes = await fetch(
        `${CHAT_API}/conversation/get-conversation-history/${userSession.userId}/${WEBCHAT_CONFIG.clientId}`
      );
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        if (historyData?.data && Array.isArray(historyData.data)) {
          const activeConversation = historyData.data.find(
            conv => conv.status === 'active'
          );
          if (activeConversation) {
            // Full CONVO_user_... ID
            freshConversationId = 
              activeConversation.conversationId ||
              freshConversationId;

            // The webchat conv_ ID
            freshVcxConversationId = 
              activeConversation.vcxConversationId ||
              activeConversation.vcx_conversation_id ||
              activeConversation.vcx_id ||
              freshVcxConversationId;

            console.log('✅ Fresh IDs fetched:', { 
              freshConversationId, 
              freshVcxConversationId 
            });
          }
        }
      }
    } catch (err) {
      console.warn('⚠️ Could not fetch fresh conversation IDs:', err);
    }
  }

    // Build conversation context — all bot messages except "Make a choice" type
    const conversationContext = chatMessages
      .filter(m => {
        if (m.type !== 'assistant') return false;
        if (!m.content || !m.content.trim()) return false;
        // Exclude short choice-prompt messages (≤ 60 chars are likely "Make a choice…")
        const cleaned = stripMarkdownToText(stripHtmlToText(m.content));
        if (cleaned.length < 10) return false;
        if (/make a choice from the list/i.test(cleaned)) return false;
        return true;
      })
      .map(m => ({
        role: 'assistant',
        content: stripMarkdownToText(stripHtmlToText(m.content || '')),
      }));

    // Build platform info for the selected connectors
    const selectedConnectorInfo = selectedPlatforms
      .map(id => connectors.find(c => c.id === id))
      .filter(Boolean)
      .map(c => ({
        integrationId: c.id,
        platform: (c.platform || '').toLowerCase(),
        name: c.name || c.platform || '',
      }));

    const brandSuffix = selectedBrand
      ? '\n\n--- Brand Kit ---\n' + [
          `Brand Name: ${selectedBrand.name}`,
          `Primary Color: ${selectedBrand.primaryColor}`,
          `Secondary Color: ${selectedBrand.secondaryColor}`,
          selectedBrand.prompt ? `Brand Instructions: ${selectedBrand.prompt}` : '',
        ].filter(Boolean).join('\n')
      : '';

    const bodyData = {
    requestId: `post-${Date.now()}`,
    userId: user?.id || user?.userId || '',
    conversationId: freshConversationId,        // ← full CONVO_user_... ID
    vcxConversationId: freshVcxConversationId,  // ← conv_... webchat ID
    botpressUserId: userSession?.userId || '',
    webchatUuid: WEBCHAT_CONFIG.uuid,
    conversationTitle: conversationTitle || '',
    userRequirements: userRequirements + brandSuffix,
    mediaType: postMediaType,
    selectedPlatforms: selectedConnectorInfo,
    conversationContext,
    businessLogo: selectedBrand?.logo || businessLogo || null,
  };

const UPLOAD_BACKEND = 'https://media.igr-consulting.com';

try {
  // Step 1: submit to upload backend — it fires n8n async and returns jobId immediately
  const startResp = await fetch(`${UPLOAD_BACKEND}/api/post-generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${process.env.REACT_APP_UPLOAD_API_KEY || '8390195655rohan'}`,
    },
    body: JSON.stringify(bodyData),
  });

  if (!startResp.ok) throw new Error(`Post generate failed (${startResp.status})`);
  const { jobId } = await startResp.json();
  if (!jobId) throw new Error('No jobId returned');

  // Step 2: subscribe to SSE — backend pushes result when n8n finishes
  const data = await new Promise((resolve, reject) => {
    const es = new EventSource(`${UPLOAD_BACKEND}/api/post-generate/progress/${jobId}`);

    es.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'ping') return;
        es.close();
        if (msg.type === 'error') return reject(new Error(msg.message || 'Post generation failed'));
        resolve(msg.data);
      } catch (e) {
        es.close();
        reject(e);
      }
    };

    es.onerror = () => {
      es.close();
      reject(new Error('SSE connection error during post generation'));
    };
  });

  // Parse post previews from n8n response
  const items = Array.isArray(data) ? data : [data];
  let posts = null;
  for (const item of items) {
    const raw = item?.json?.output || item?.output || '';
    if (raw.includes('__POST_PREVIEW__') && raw.includes('__END_POST_PREVIEW__')) {
      try {
        const jsonStr = raw
          .split('__POST_PREVIEW__')[1]
          .split('__END_POST_PREVIEW__')[0]
          .trim();
        posts = JSON.parse(jsonStr);
      } catch (e) {
        console.error('Failed to parse post preview:', e);
      }
      break;
    }
  }

  const KNOWN_PLATFORMS = ['facebook', 'linkedin', 'twitter', 'x', 'instagram', 'youtube', 'pinterest'];

  if (posts && Array.isArray(posts) && posts.length > 0) {
    posts.forEach(postItem => {
      if (!postItem.platform) return;
      // Skip items whose platform wasn't selected or isn't a recognised social platform
      const platformLower = (postItem.platform || '').toLowerCase();
      if (!KNOWN_PLATFORMS.includes(platformLower)) return;
      if (!selectedConnectorInfo.some(c => c.platform === platformLower)) return;
      const postMsg = {
        id: `post-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: 'post_preview',
        timestamp: new Date(),
        platform: platformLower,
        integrationId: postItem.integrationId || '',
        caption: postItem.caption || '',
        hashtags: Array.isArray(postItem.hashtags) ? postItem.hashtags : [],
        mediaUrl: postItem.mediaUrl || null,
        mediaType: postItem.mediaType || 'image',
        postStatus: 'idle',
        postError: null,
      };
      setExtraMessages(prev => {
        if (prev.some(m => m.caption === postMsg.caption && m.platform === postMsg.platform)) {
          return prev;
        }
        return [...prev, postMsg];
      });
    });
    setSelectedPlatforms([]);
    setPostMediaType('image');
  } else {
    toast.error('No post previews returned from generation. Please try again.');
  }

} catch (err) {
  toast.error(`Post generation failed: ${err.message}`);
} finally {
  setIsGeneratingPost(false);
}
};

  // ── NEW: Social Media Post Generation (one-by-one) ──────────────────────────

  const SOCIAL_POSTS_WEBHOOK = "https://workflow.igr-consulting.com/webhook/social-media-posts";

  /**
   * Called when user submits the MarketingStrategyForm.
   * Fires one n8n request per (day × platform) combination, sequentially,
   * appending each result to socialPosts as it arrives.
   */
  const handleSocialPostsFormSubmit = async (messageId, formData) => {
    const message = chatMessages.find(m => m.id === messageId);
    if (!message) return;

    const strategy = marketingStrategies[messageId]?.strategy || {};
    const postizToken = sessionStorage.getItem('postiz_token');

    // Build the day-platform matrix
    const numberOfDays = parseInt(formData.numberOfDays, 10) || 1;
    const platforms = formData.platforms || [];
    const daysOfWeek = formData.daysOfWeek || [];
    const publishTime = formData.publishTime || '09:00';
    const [pubHour, pubMinute] = publishTime.split(':').map(Number);

    // All combinations: day 1 × platforms, day 2 × platforms, …
    const combinations = [];
    for (let day = 1; day <= numberOfDays; day++) {
      for (const platform of platforms) {
        combinations.push({ day, platform });
      }
    }

    // Close form, open preview with all slots pending
    setMarketingForms(prev => ({
      ...prev,
      [messageId]: { open: false, submitting: false, submitted: true },
    }));
    setSocialPosts(prev => ({
      ...prev,
      [messageId]: {
        posts: [],
        pendingDays: [...combinations],
        generating: true,
        publishTime,
        daysOfWeek,
      },
    }));

    // Helper: compute schedule date for a given (dayIndex, platform)
    const DAY_NAME_TO_JS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const getScheduleDate = (dayIndex) => {
      const d = new Date();
      d.setHours(pubHour, pubMinute, 0, 0);
      if (daysOfWeek.length > 0) {
        const targetDayName = daysOfWeek[dayIndex % daysOfWeek.length];
        const targetDayJs = DAY_NAME_TO_JS[targetDayName];
        const occurrenceNeeded = Math.floor(dayIndex / daysOfWeek.length);
        let found = 0;
        let daysToAdd = 1;
        while (daysToAdd < 365) {
          const candidate = new Date();
          candidate.setDate(candidate.getDate() + daysToAdd);
          if (candidate.getDay() === targetDayJs) {
            if (found === occurrenceNeeded) {
              d.setDate(d.getDate() + daysToAdd);
              break;
            }
            found++;
          }
          daysToAdd++;
        }
      } else {
        d.setDate(d.getDate() + (dayIndex + 1));
      }
      return d;
    };

    // Fire requests sequentially
    for (let i = 0; i < combinations.length; i++) {
      const { day, platform } = combinations[i];
      const scheduleDate = getScheduleDate(i);
      const postId = `social-post-${messageId}-day${day}-${platform}-${Date.now()}`;

      try {
        const payload = {
          strategy,
          day,
          platform,
          userRequirements: formData.userRequirements || '',
          targetAudience: formData.targetAudience || '',
          publishTime,
          scheduledDate: scheduleDate.toISOString(),
          campaignName: strategy.executiveSummary?.substring?.(0, 60) || '',
        };

        const resp = await fetch(SOCIAL_POSTS_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          mode: 'cors',
          credentials: 'omit',
          body: JSON.stringify(payload),
        });

        if (!resp.ok) throw new Error(`Webhook error (${resp.status})`);
        const data = await resp.json();

        // Expected shape: { caption, hashtags: string | string[], imageUrl }
        const result = Array.isArray(data) ? data[0] : data;

        // Before the for loop, build a platform→connectorId lookup map
        const platformToConnectorId = {};
        connectors.forEach(c => {
          const key = (c.platform || '').toLowerCase();
          if (key && !platformToConnectorId[key]) {
            platformToConnectorId[key] = c.id;
          }
        });

        // Inside the loop, before try block:
        const matchingConnectorId = 
          platformToConnectorId[platform.toLowerCase()] ||
          connectors.find(c => 
            (c.platform || '').toLowerCase().includes(platform.toLowerCase()) ||
            platform.toLowerCase().includes((c.platform || '').toLowerCase())
          )?.id || '';

        const newPost = {
          id: postId,
          day,
          platform,
          integrationId: matchingConnectorId,
          caption: result.caption || '',
          hashtags: Array.isArray(result.hashtags) ? result.hashtags : (result.hashtags || '').split(' ').filter(Boolean),
          imageUrl: result.imageUrl || result.image_url || null,
          scheduledDate: scheduleDate.toISOString(),
          status: 'ready',  // ready | regenerating | approving | approved | scheduled | error
        };

        setSocialPosts(prev => {
          const current = prev[messageId] || {};
          const remaining = (current.pendingDays || []).filter(
            p => !(p.day === day && p.platform === platform)
          );
          return {
            ...prev,
            [messageId]: {
              ...current,
              posts: [...(current.posts || []), newPost],
              pendingDays: remaining,
            },
          };
        });

      } catch (err) {
        console.error(`Failed to generate Day ${day} / ${platform}:`, err);
        // Add error card so user sees it
        setSocialPosts(prev => {
          const current = prev[messageId] || {};
          const remaining = (current.pendingDays || []).filter(
            p => !(p.day === day && p.platform === platform)
          );
          return {
            ...prev,
            [messageId]: {
              ...current,
              posts: [
                ...(current.posts || []),
                {
                  id: postId,
                  day,
                  platform,
                  caption: `Failed to generate: ${err.message}`,
                  hashtags: [],
                  imageUrl: null,
                  scheduledDate: scheduleDate.toISOString(),
                  status: 'error',
                },
              ],
              pendingDays: remaining,
            },
          };
        });
      }
    }

    // Mark generation complete
    setSocialPosts(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], generating: false },
    }));
  };

  // ── PINTEREST BOARDS ────────────────────────────────────────────────────────

  const isPinterestIntegration = (integrationId) => {
    const c = connectors.find(x => x.id === integrationId);
    return !!c && [c.platform, c.identifier].some(v => (v || '').toLowerCase() === 'pinterest');
  };

  /** Resolves the Postiz connector for a marketing-strategy social post */
  const resolveSocialPostIntegrationId = (postData) => {
    const platformLower = (postData.platform || '').toLowerCase();
    return postData.integrationId ||
      connectors.find(c => (c.platform || '').toLowerCase() === platformLower)?.id ||
      connectors.find(c => (c.name || '').toLowerCase().includes(platformLower))?.id ||
      connectors.find(c => platformLower.includes((c.platform || '').toLowerCase()))?.id ||
      connectors.find(c => platformLower.includes((c.name || '').toLowerCase()))?.id;
  };

  /** Returns a toast-ready error when a Pinterest post has no media or no board selected, else null */
  const getPinterestPostError = (integrationId, hasMedia) => {
    if (!isPinterestIntegration(integrationId)) return null;
    if (!hasMedia) return 'Pinterest pins need an image or video. Add media to this post before posting.';
    if (!pinterestBoardIds[integrationId]) return 'Select a Pinterest board before posting.';
    return null;
  };

  // Fetch boards once for every Pinterest account that has a generated post card
  useEffect(() => {
    const postizToken = sessionStorage.getItem('postiz_token');
    if (!postizToken) return;
    const ids = [
      ...extraMessages.filter(m => m.type === 'post_preview').map(m => m.integrationId),
      ...Object.values(socialPosts).flatMap(s => (s?.posts || []).map(resolveSocialPostIntegrationId)),
    ];
    new Set(ids).forEach(async (integrationId) => {
      if (!integrationId || !isPinterestIntegration(integrationId)) return;
      if (pinterestBoardsRequestedRef.current.has(integrationId)) return;
      pinterestBoardsRequestedRef.current.add(integrationId);
      const result = await postizAPI.getProviderFunction(postizToken, integrationId, 'boards');
      if (!result.success) toast.error(`Couldn't load Pinterest boards: ${result.error}`);
      setPinterestBoards(prev => ({ ...prev, [integrationId]: result.success ? (result.data || []) : [] }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraMessages, socialPosts, connectors]);

  /**
   * User clicks "Approve & Schedule" on a single post.
   * Uploads image (if any) to Postiz, schedules the post, marks as scheduled.
   */
  const handleApproveSocialPost = async (messageId, postId) => {
    const postData = socialPosts[messageId]?.posts?.find(p => p.id === postId);
    if (!postData) return;
    const postizToken = sessionStorage.getItem('postiz_token');
    if (!postizToken) {
      toast.error('Not connected to Postiz. Please go to Performance Marketing to connect.');
      return;
    }
    const pinterestError = getPinterestPostError(resolveSocialPostIntegrationId(postData), !!postData.imageUrl);
    if (pinterestError) { toast.error(pinterestError); return; }
    const mediaError = getPlatformMediaError(
      (postData.platform || '').toLowerCase(),
      postData.imageUrl ? [{ url: postData.imageUrl, type: 'image' }] : []
    );
    if (mediaError) { toast.error(mediaError); return; }

    // Mark as approving
    setSocialPosts(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        posts: prev[messageId].posts.map(p =>
          p.id === postId ? { ...p, status: 'approving' } : p
        ),
      },
    }));

    try {
      let images = [];

      if (postData.imageUrl) {
        // Mirror exactly how handleInlineScheduleSubmit handles hosted URLs
        try {
          const imgResp = await fetch(postData.imageUrl);
          if (!imgResp.ok) throw new Error(`Image fetch failed (${imgResp.status})`);
          const imgBlob = await imgResp.blob();
          const mimeType = imgBlob.type || 'image/jpeg';
          const ext = mimeType.split('/')[1] || 'jpg';
          const file = new File([imgBlob], `post-image.${ext}`, { type: mimeType });
          const uploadResult = await postizAPI.uploadMedia(postizToken, file);
          if (!uploadResult.success) throw new Error(uploadResult.error || 'unknown');
          images = [{ id: uploadResult.id || uploadResult.url, path: uploadResult.url }];
        } catch (fetchErr) {
          console.warn('Image fetch failed, passing URL directly:', fetchErr.message);
          images = [{ id: postData.imageUrl, path: postData.imageUrl }];
        }
      }

      const integrationId = resolveSocialPostIntegrationId(postData);

      console.log('Resolved integrationId:', integrationId);

      if (!integrationId) {
        console.log("💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀💀");
        throw new Error(
          `No connected account found for ${postData.platform}. Please connect it in Performance Marketing.`
        );
      }

      const caption = [postData.caption, ...postData.hashtags].filter(Boolean).join('\n');

      // Mirror handleInlineScheduleSubmit exactly
      const result = await postizAPI.schedulePost(postizToken, {
        text: caption,
        images,
        integrationIds: [integrationId],
        integrations: connectors,
        pinterestBoardIds,
        publishedAt: postData.scheduledDate,
        postNow: false,
      });

      if (!result.success) throw new Error(result.error || 'Scheduling failed');

      setSocialPosts(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          posts: prev[messageId].posts.map(p =>
            p.id === postId ? { ...p, status: 'scheduled' } : p
          ),
        },
      }));
      toast.success(`Day ${postData.day} post scheduled for ${new Date(postData.scheduledDate).toLocaleString()}!`);

    } catch (err) {
      toast.error(`Failed to schedule: ${err.message}`);
      setSocialPosts(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          posts: prev[messageId].posts.map(p =>
            p.id === postId ? { ...p, status: 'ready' } : p
          ),
        },
      }));
    }
  };

  /**
   * User clicks "Regenerate" on a single post.
   * Re-fires the webhook for just that one post and replaces it.
   */
  const handleRegenerateSocialPost = async (messageId, postId) => {
    const postData = socialPosts[messageId]?.posts?.find(p => p.id === postId);
    if (!postData) return;

    const strategy = marketingStrategies[messageId]?.strategy || {};
    const publishTime = socialPosts[messageId]?.publishTime || '09:00';

    setSocialPosts(prev => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        posts: prev[messageId].posts.map(p =>
          p.id === postId ? { ...p, status: 'regenerating' } : p
        ),
      },
    }));

    try {
      const resp = await fetch(SOCIAL_POSTS_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          strategy,
          day: postData.day,
          platform: postData.platform,
          publishTime,
          scheduledDate: postData.scheduledDate,
          regenerate: true,
          previousCaption: postData.caption,
        }),
      });

      if (!resp.ok) throw new Error(`Webhook error (${resp.status})`);
      const data = await resp.json();
      const result = Array.isArray(data) ? data[0] : data;

      setSocialPosts(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          posts: prev[messageId].posts.map(p =>
            p.id === postId
              ? {
                  ...p,
                  caption: result.caption || p.caption,
                  hashtags: Array.isArray(result.hashtags)
                    ? result.hashtags
                    : (result.hashtags || '').split(' ').filter(Boolean),
                  imageUrl: result.imageUrl || result.image_url || p.imageUrl,
                  status: 'ready',
                }
              : p
          ),
        },
      }));
      toast.success(`Day ${postData.day} post regenerated!`);

    } catch (err) {
      toast.error(`Regeneration failed: ${err.message}`);
      setSocialPosts(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          posts: prev[messageId].posts.map(p =>
            p.id === postId ? { ...p, status: 'ready' } : p
          ),
        },
      }));
    }
  };



  // ════════════════════════════════════════════════════════════════════════════
  // NEW: MARKETING STRATEGY V2 — Strategy Layer & 3 Execution Channels
  // ════════════════════════════════════════════════════════════════════════════

  /** Step 1: Opens the new strategy generation form (replaces old behavior) */
  const handleGenerateMarketingStrategy = (messageId) => {
    setStrategyForms(prev => ({
      ...prev,
      [messageId]: { open: true, submitting: false, submitted: false },
    }));
  };

  /** Step 2: Submits strategy form to backend — receives full strategy document */
  const handleStrategyFormSubmit = async (messageId, formData) => {
    const message = chatMessages.find(m => m.id === messageId);
    if (!message) return;

    setStrategyForms(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], submitting: true },
    }));

    const payload = {
      botMessage: message.isHtml
        ? message.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : (message.content || ''),
      campaignName: formData.campaignName,
      businessGoals: formData.businessGoals,
      targetAudience: formData.targetAudience,
      budgetRange: formData.budgetRange,
      campaignDuration: formData.campaignDuration,
      toneOfVoice: formData.toneOfVoice,
      competitors: formData.competitors,
      userRequirements: formData.userRequirements,
    };

    try {
      const STRATEGY_WEBHOOK = "https://workflow.igr-consulting.com/webhook/marketing-strategy";
      const resp = await fetch(STRATEGY_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!resp.ok) throw new Error(`Webhook error (${resp.status})`);

      const data = await resp.json();
      console.log('Raw N8N response:', data);
      
      const strategy = transformStrategyResponse(data);
      
      if (!strategy) {
        throw new Error('Failed to parse strategy response. Invalid format from webhook.');
      }

      console.log('Final strategy object:', strategy);

      setStrategyForms(prev => ({
        ...prev,
        [messageId]: { open: false, submitting: false, submitted: true },
      }));
      
      setMarketingStrategies(prev => ({
        ...prev,
        [messageId]: { 
          strategy, 
          formPayload: payload,
          open: true, 
          editing: false, 
          approved: false, 
          submitting: false 
        },
      }));
      
      toast.success('Strategy generated successfully! Review and approve to continue.');
      
    } catch (err) {
      console.error('Strategy generation error:', err);
      toast.error(`Strategy generation failed: ${err.message}`);
      setStrategyForms(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], submitting: false },
      }));
    }
  };

  /** Step 3: User edits a strategy section inline */
  const handleStrategySectionEdit = (messageId, sectionName, newContent) => {
    setMarketingStrategies(prev => {
      const current = prev[messageId]?.strategy || {};
      return {
        ...prev,
        [messageId]: {
          ...prev[messageId],
          strategy: { ...current, [sectionName]: newContent },
          editing: true,
        },
      };
    });
  };

  /** Step 3b: User regenerates a specific section (placeholder — calls backend) */
  const handleRegenerateStrategySection = async (messageId, sectionName) => {
    const currentStrategy = marketingStrategies[messageId]?.strategy;
    if (!currentStrategy) return;

    setMarketingStrategies(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], submitting: true, regeneratingSection: sectionName },
    }));

    try {
      const STRATEGY_WEBHOOK = "https://workflow.igr-consulting.com/webhook/marketing-strategy";
      const savedPayload = marketingStrategies[messageId]?.formPayload || {};
      const resp = await fetch(STRATEGY_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...savedPayload,
          currentStrategy,
          regenerateSection: sectionName,
          regenerate: true,
        }),
      });

      if (!resp.ok) throw new Error(`Webhook error (${resp.status})`);

      const data = await resp.json();
      const updated = transformStrategyResponse(data);

      // Merge: only replace the regenerated section if the webhook returned it
      const newSectionValue = updated?.[sectionName] ?? currentStrategy[sectionName];

      setMarketingStrategies(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          strategy: { ...currentStrategy, [sectionName]: newSectionValue },
          submitting: false,
          regeneratingSection: null,
        },
      }));

      toast.success(`Section regenerated!`);
    } catch (err) {
      toast.error(`Regeneration failed: ${err.message}`);
      setMarketingStrategies(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], submitting: false, regeneratingSection: null },
      }));
    }
  };

  /** Step 4: User approves strategy → moves to channel selection */
  const handleApproveStrategy = (messageId) => {
    setMarketingStrategies(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], approved: true, open: false },
    }));
    setChannelSelection(prev => ({
      ...prev,
      [messageId]: { open: true, selectedChannels: [] },
    }));
    // Initialize campaign status
    setCampaignStatus(prev => ({
      ...prev,
      [messageId]: { strategyApproved: true },
    }));
  };

  /** Step 4b: User discards strategy → re-opens form */
  const handleRejectStrategy = (messageId) => {
    setMarketingStrategies(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
    setStrategyForms(prev => ({
      ...prev,
      [messageId]: { open: true, submitting: false, submitted: false },
    }));
  };

  /** Step 5: User toggles a channel in the selection modal */
  const handleChannelToggle = (messageId, channelKey) => {
    setChannelSelection(prev => {
      const current = prev[messageId] || { open: true, selectedChannels: [] };
      const selected = current.selectedChannels || [];
      const updated = selected.includes(channelKey)
        ? selected.filter(c => c !== channelKey)
        : [...selected, channelKey];
      return {
        ...prev,
        [messageId]: { ...current, selectedChannels: updated },
      };
    });
  };

  /** Step 5b: User clicks Continue from channel selection → opens channel forms */
  const handleChannelContinue = (messageId) => {
    const selected = channelSelection[messageId]?.selectedChannels || [];
    setChannelSelection(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], open: false },
    }));

    // Open the appropriate form for each selected channel
    selected.forEach(channel => {
      if (channel === 'offline') {
        setOfflineForms(prev => ({
          ...prev,
          [messageId]: { open: true, submitting: false },
        }));
      } else if (channel === 'onlineAds') {
        setAdsForms(prev => ({
          ...prev,
          [messageId]: { open: true, submitting: false },
        }));
      } else if (channel === 'socialMedia') {
        // Use existing social media form
        setMarketingForms(prev => ({
          ...prev,
          [messageId]: { open: true, submitting: false, submitted: false },
        }));
      }
    });
  };

  /** Step 5c: User goes back from channel selection to strategy document */
  const handleChannelBack = (messageId) => {
    setChannelSelection(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
    setMarketingStrategies(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], approved: false, open: true },
    }));
  };

  // ── OFFLINE MARKETING CHANNEL ───────────────────────────────────────────────

  const handleOfflineFormSubmit = async (messageId, formData) => {
  const strategy = marketingStrategies[messageId]?.strategy;

  setOfflineForms(prev => ({
    ...prev,
    [messageId]: { ...prev[messageId], submitting: true },
  }));

  // The material types the user selected, e.g. ['Flyer', 'Poster', 'Billboard']
  const materialTypes = formData.materialTypes || [];

  if (materialTypes.length === 0) {
    setOfflineForms(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], submitting: false },
    }));
    return;
  }

  // Close the form and open the preview immediately with all types pending
  setOfflineForms(prev => ({
    ...prev,
    [messageId]: { open: false, submitting: false },
  }));
  setOfflineMarketing(prev => ({
    ...prev,
    [messageId]: {
      materials: [],
      pendingTypes: [...materialTypes],   // all types are pending at first
      open: true,
      submitting: true,
      approved: false,
    },
  }));
  setCampaignStatus(prev => ({
    ...prev,
    [messageId]: {
      ...prev[messageId],
      offline: { approved: 0, total: materialTypes.length, status: 'draft' },
    },
  }));

  const OFFLINE_WEBHOOK = 'https://workflow.igr-consulting.com/webhook/marketing-offline';

  // Fire one request per material type, sequentially, appending results live
  for (const materialType of materialTypes) {
    const payload = {
      strategy,
      offlineFormData: {
        ...formData,
        materialTypes: [materialType],   // single type per call
      },
      materialType,                       // convenience field for n8n
    };

    try {
      const resp = await fetch(OFFLINE_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) throw new Error(`Webhook error (${resp.status})`);

      const data = await resp.json();

      // n8n should return a single material object or a one-element array
      const raw = Array.isArray(data) ? data[0] : data;
      const material = raw?.material || raw?.result || raw;

      // Normalise: ensure required fields exist
      const normalised = {
        id: material.id || `mat-${materialType.toLowerCase()}-${Date.now()}`,
        type: material.type || materialType,
        headline: material.headline || '',
        subheadline: material.subheadline || '',
        bodyCopy: material.bodyCopy || material.body_copy || material.content || '',
        cta: material.cta || '',
        dimensions: material.dimensions || formData.dimensions || '',
        location: material.location || formData.location || '',
        brandPillar: material.brandPillar || material.brand_pillar || '',
        quantity: material.quantity || formData.quantity || '',
        finish: material.finish || '',
        visualDescription: material.visualDescription || material.visual_description || '',
        imagePrompt: material.imagePrompt || material.image_prompt || '',
        imageUrl: material.imageUrl || material.image_url || material.mockupUrl || null,
        generatedAssets: material.generatedAssets || {},
        brandColors: (() => {
          const raw = material.brandColors;
          if (!raw) return [];
          if (Array.isArray(raw)) return raw.filter(Boolean);
          if (typeof raw === 'string') return raw.split(/[,\s]+/).map(s => s.trim()).filter(s => s.startsWith('#') || s.length > 0);
          return [];
        })(),
        placementNotes: material.placementNotes || '',
        status: 'draft',
      };

      // Append the completed material and remove it from pending
      setOfflineMarketing(prev => {
        const current = prev[messageId] || {};
        const newPending = (current.pendingTypes || []).filter(t => t !== materialType);
        return {
          ...prev,
          [messageId]: {
            ...current,
            materials: [...(current.materials || []), normalised],
            pendingTypes: newPending,
            submitting: newPending.length > 0,   // still generating if more remain
          },
        };
      });

    } catch (err) {
      console.error(`Failed to generate ${materialType}:`, err);
      toast.error(`Failed to generate ${materialType}: ${err.message}`);

      // Remove from pending even on error (show error state or just skip)
      setOfflineMarketing(prev => {
        const current = prev[messageId] || {};
        const newPending = (current.pendingTypes || []).filter(t => t !== materialType);
        return {
          ...prev,
          [messageId]: {
            ...current,
            pendingTypes: newPending,
            submitting: newPending.length > 0,
          },
        };
      });
    }
  }
};

  /** Approves a single offline material */
  const handleApproveOfflineMaterial = (messageId, materialId) => {
    setOfflineMarketing(prev => {
      const updated = [...(prev[messageId]?.materials || [])];
      const idx = updated.findIndex(m => m.id === materialId);
      if (idx >= 0) updated[idx] = { ...updated[idx], status: 'approved' };
      const approvedCount = updated.filter(m => m.status === 'approved').length;
      return {
        ...prev,
        [messageId]: { ...prev[messageId], materials: updated },
      };
    });
    setCampaignStatus(prev => {
      const total = prev[messageId]?.offline?.total || 0;
      const currentApproved = prev[messageId]?.offline?.approved || 0;
      return {
        ...prev,
        [messageId]: {
          ...prev[messageId],
          offline: {
            ...prev[messageId]?.offline,
            approved: Math.min(currentApproved + 1, total),
            status: currentApproved + 1 >= total ? 'ready' : 'draft',
          },
        },
      };
    });
  };

  const handleDownloadOfflineMaterial = (messageId, materialId, format) => {
    const material = offlineMarketing[messageId]?.materials?.find(m => m.id === materialId);
    if (!material) {
      toast.error('Material not found.');
      return;
    }

    const imageUrl = material.generatedAssets?.designMockup || material.imageUrl;

    if (format === 'png') {
      if (!imageUrl) { toast.error('No image available to download.'); return; }
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = `${material.type}-${material.id}.png`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Downloading ${material.type} as PNG…`);
      return;
    }

    if (format === 'pdf') {
      const toastId = toast.loading(`Generating ${material.type} PDF…`);

      const generatePdf = (imgData, imgWidth, imgHeight) => {
        try {
          const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
          const pageW = pdf.internal.pageSize.getWidth();   // 210
          const pageH = pdf.internal.pageSize.getHeight();  // 297
          const margin = 14;
          const contentW = pageW - margin * 2;
          let y = margin;

          // ── Helper: add text with auto word-wrap, returns new Y ──────────
          const addText = (text, fontSize, isBold, color, maxWidth, lineHeight) => {
            if (!text) return y;
            pdf.setFontSize(fontSize);
            pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
            pdf.setTextColor(...color);
            const lines = pdf.splitTextToSize(String(text), maxWidth);
            lines.forEach(line => {
              if (y + lineHeight > pageH - margin) {
                pdf.addPage();
                y = margin;
              }
              pdf.text(line, margin, y);
              y += lineHeight;
            });
            return y;
          };

          // ── Helper: thin horizontal rule ─────────────────────────────────
          const addRule = (color = [220, 220, 220]) => {
            if (y + 2 > pageH - margin) { pdf.addPage(); y = margin; }
            pdf.setDrawColor(...color);
            pdf.setLineWidth(0.3);
            pdf.line(margin, y, pageW - margin, y);
            y += 4;
          };

          // ════════════════════════════════════════════════════════════════
          // PAGE 1 — HEADER + IMAGE
          // ════════════════════════════════════════════════════════════════

          // Orange accent bar at top
          pdf.setFillColor(255, 107, 53);
          pdf.rect(0, 0, pageW, 2, 'F');
          y = 8;

          // "INFINIUM" label
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 107, 53);
          pdf.text('INFINIUM — OFFLINE MARKETING', margin, y);
          y += 5;

          // Material type chip
          const typeLabel = (material.type || 'Material').toUpperCase();
          pdf.setFillColor(255, 107, 53);
          pdf.roundedRect(margin, y, 22, 5, 1.5, 1.5, 'F');
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(255, 255, 255);
          pdf.text(typeLabel, margin + 2, y + 3.5);
          y += 9;

          // Headline
          addText(material.headline, 18, true, [15, 15, 30], contentW, 7);
          y += 1;

          // Subheadline
          if (material.subheadline) {
            addText(material.subheadline, 11, false, [100, 100, 120], contentW, 5.5);
            y += 2;
          }

          addRule();

          // Image (if available)
          if (imgData) {
            const maxImgH = 90;
            const ratio = Math.min(contentW / imgWidth, maxImgH / imgHeight);
            const drawW = imgWidth * ratio;
            const drawH = imgHeight * ratio;
            const imgX = margin + (contentW - drawW) / 2;

            if (y + drawH > pageH - margin) { pdf.addPage(); y = margin; }
            pdf.addImage(imgData, 'PNG', imgX, y, drawW, drawH);
            y += drawH + 6;
          }

          // ════════════════════════════════════════════════════════════════
          // COPY SECTION
          // ════════════════════════════════════════════════════════════════
          addRule();

          if (material.bodyCopy) {
            addText('Body Copy', 8, true, [150, 150, 160], contentW, 4.5);
            y += 1;
            addText(material.bodyCopy, 10, false, [40, 40, 55], contentW, 5);
            y += 4;
          }

          if (material.cta) {
            // CTA pill
            const ctaText = material.cta;
            const ctaW = Math.min(contentW, 70);
            if (y + 10 > pageH - margin) { pdf.addPage(); y = margin; }
            pdf.setFillColor(255, 107, 53);
            pdf.roundedRect(margin, y, ctaW, 8, 2, 2, 'F');
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(ctaText, margin + ctaW / 2, y + 5.2, { align: 'center' });
            y += 13;
          }

          // ════════════════════════════════════════════════════════════════
          // SPECS GRID
          // ════════════════════════════════════════════════════════════════
          addRule();
          addText('Specifications', 8, true, [150, 150, 160], contentW, 4.5);
          y += 2;

          const specs = [
            ['Dimensions',   material.dimensions],
            ['Location',     material.location],
            ['Finish',       material.finish],
            ['Quantity',     material.quantity],
            ['Brand Pillar', material.brandPillar],
          ].filter(([, v]) => v);

          const colW = contentW / 2;
          specs.forEach(([label, value], i) => {
            const col = i % 2;
            const row = Math.floor(i / 2);
            const bx = margin + col * colW;
            const by = y + row * 10;
            if (by + 10 > pageH - margin) { pdf.addPage(); y = margin; }
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(150, 150, 160);
            pdf.text(label.toUpperCase(), bx, by);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(40, 40, 55);
            pdf.text(String(value), bx, by + 4.5);
          });
          y += Math.ceil(specs.length / 2) * 10 + 4;

          // Brand colors swatches
          const safeColors = Array.isArray(material.brandColors) ? material.brandColors : [];
          if (safeColors.length > 0) {
            if (y + 12 > pageH - margin) { pdf.addPage(); y = margin; }
            addText('Brand Colors', 8, true, [150, 150, 160], contentW, 4.5);
            y += 1;
            safeColors.forEach((hex, i) => {
              const r = parseInt(hex.slice(1, 3), 16) || 0;
              const g = parseInt(hex.slice(3, 5), 16) || 0;
              const b = parseInt(hex.slice(5, 7), 16) || 0;
              pdf.setFillColor(r, g, b);
              pdf.roundedRect(margin + i * 22, y, 18, 6, 1.5, 1.5, 'F');
              pdf.setFontSize(6.5);
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(100, 100, 110);
              pdf.text(hex.toUpperCase(), margin + i * 22, y + 9);
            });
            y += 14;
          }

          // ════════════════════════════════════════════════════════════════
          // VISUAL BRIEF + PLACEMENT
          // ════════════════════════════════════════════════════════════════
          if (material.visualDescription) {
            addRule();
            addText('Visual Brief', 8, true, [150, 150, 160], contentW, 4.5);
            y += 1;
            addText(material.visualDescription, 9, false, [60, 60, 75], contentW, 4.8);
            y += 3;
          }

          if (material.placementNotes) {
            addRule();
            addText('Placement Notes', 8, true, [150, 150, 160], contentW, 4.5);
            y += 1;
            addText(material.placementNotes, 9, false, [60, 60, 75], contentW, 4.8);
            y += 3;
          }

          // ════════════════════════════════════════════════════════════════
          // FOOTER on every page
          // ════════════════════════════════════════════════════════════════
          const totalPages = pdf.internal.getNumberOfPages();
          for (let p = 1; p <= totalPages; p++) {
            pdf.setPage(p);
            pdf.setFillColor(255, 107, 53);
            pdf.rect(0, pageH - 1.5, pageW, 1.5, 'F');
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(160, 160, 170);
            pdf.text(
              `${material.type} • ${material.dimensions || ''} • Infinium Intelligence`,
              pageW / 2,
              pageH - 4,
              { align: 'center' }
            );
            pdf.text(`Page ${p} of ${totalPages}`, pageW - margin, pageH - 4, { align: 'right' });
          }

          pdf.save(`${material.type}-${material.id}.pdf`);
          toast.update(toastId, {
            render: `${material.type} PDF downloaded!`,
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });
        } catch (err) {
          toast.update(toastId, {
            render: `PDF failed: ${err.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
        }
      };

      // Load image first, then generate — fall back gracefully if no image
      if (imageUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          canvas.getContext('2d').drawImage(img, 0, 0);
          generatePdf(canvas.toDataURL('image/png'), img.width, img.height);
        };
        img.onerror = () => {
          // CORS blocked — generate PDF without image
          generatePdf(null, 0, 0);
        };
        img.src = imageUrl;
      } else {
        generatePdf(null, 0, 0);
      }
      return;
    }

    toast.info(`Format "${format}" is not supported yet.`);
  };

  /** Regenerates an offline material */
  const handleRegenerateOfflineMaterial = async (messageId, materialId) => {
    const currentMaterial = offlineMarketing[messageId]?.materials?.find(m => m.id === materialId);
    if (!currentMaterial) return;

    const strategy = marketingStrategies[messageId]?.strategy;
    const formData = {}; // retrieve from state if you store offlineFormData per messageId

    const OFFLINE_WEBHOOK = 'https://workflow.igr-consulting.com/webhook/marketing-offline';

    // Mark material as regenerating
    setOfflineMarketing(prev => {
      const updated = (prev[messageId]?.materials || []).map(m =>
        m.id === materialId ? { ...m, regenerating: true } : m
      );
      return { ...prev, [messageId]: { ...prev[messageId], materials: updated } };
    });

    try {
      const resp = await fetch(OFFLINE_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          strategy,
          offlineFormData: { ...formData, materialTypes: [currentMaterial.type] },
          materialType: currentMaterial.type,
          regenerate: true,
          previousMaterialId: materialId,
        }),
      });

      if (!resp.ok) throw new Error(`Webhook error (${resp.status})`);
      const data = await resp.json();
      const raw = Array.isArray(data) ? data[0] : data;
      const material = raw?.material || raw?.result || raw;

      setOfflineMarketing(prev => {
        const updated = (prev[messageId]?.materials || []).map(m =>
          m.id === materialId
            ? { ...m, ...material, id: materialId, status: 'draft', regenerating: false }
            : m
        );
        return { ...prev, [messageId]: { ...prev[messageId], materials: updated } };
      });

      toast.success(`${currentMaterial.type} regenerated!`);
    } catch (err) {
      toast.error(`Regeneration failed: ${err.message}`);
      setOfflineMarketing(prev => {
        const updated = (prev[messageId]?.materials || []).map(m =>
          m.id === materialId ? { ...m, regenerating: false } : m
        );
        return { ...prev, [messageId]: { ...prev[messageId], materials: updated } };
      });
    }
  };

  /** Regenerates all offline materials one by one */
  const handleRegenerateAllOfflineMaterials = async (messageId) => {
    const materials = offlineMarketing[messageId]?.materials;
    if (!materials || materials.length === 0) return;

    toast.info(`Regenerating ${materials.length} material${materials.length > 1 ? 's' : ''}…`);

    for (const material of materials) {
      await handleRegenerateOfflineMaterial(messageId, material.id);
    }

    toast.success('All materials regenerated!');
  };

  /** Goes back from offline preview to channel selection */
  const handleOfflineBack = (messageId) => {
    setOfflineMarketing(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
    setChannelSelection(prev => ({
      ...prev,
      [messageId]: { open: true, selectedChannels: channelSelection[messageId]?.selectedChannels || [] },
    }));
  };

  // ── ONLINE ADS CHANNEL ──────────────────────────────────────────────────────

  /** Submits ads form to generate campaigns */
  const handleAdsFormSubmit = async (messageId, formData) => {
    const strategy = marketingStrategies[messageId]?.strategy;

    setAdsForms(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], submitting: true },
    }));

    const payload = {
      strategy,
      adsFormData: formData,
    };

    try {
      const ADS_CREATIVE_WEBHOOK = "https://workflow.igr-consulting.com/webhook/marketing-ads-creative";
      const resp = await fetch(ADS_CREATIVE_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`Ads creative webhook error (${resp.status})`);

      const data = await resp.json();
      const campaigns = Array.isArray(data) ? data : (data.campaigns || data.result || []);

      setAdsForms(prev => ({
        ...prev,
        [messageId]: { open: false, submitting: false },
      }));
      setOnlineAds(prev => ({
        ...prev,
        [messageId]: { campaigns, open: true, submitting: false, approved: false },
      }));
      setCampaignStatus(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          onlineAds: { approved: 0, total: campaigns.length, status: 'draft' },
        },
      }));
    } catch (err) {
      toast.error(`Ads campaign generation failed: ${err.message}`);
      setAdsForms(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], submitting: false },
      }));
    }
  };

  /** Approves an ad campaign */
  const handleApproveAdsCampaign = (messageId, campaignId) => {
    setOnlineAds(prev => {
      const updated = [...(prev[messageId]?.campaigns || [])];
      const idx = updated.findIndex(c => c.id === campaignId);
      if (idx >= 0) updated[idx] = { ...updated[idx], status: 'approved' };
      return {
        ...prev,
        [messageId]: { ...prev[messageId], campaigns: updated },
      };
    });
    setCampaignStatus(prev => {
      const total = prev[messageId]?.onlineAds?.total || 0;
      const currentApproved = prev[messageId]?.onlineAds?.approved || 0;
      return {
        ...prev,
        [messageId]: {
          ...prev[messageId],
          onlineAds: {
            ...prev[messageId]?.onlineAds,
            approved: Math.min(currentApproved + 1, total),
            status: currentApproved + 1 >= total ? 'approved' : 'draft',
          },
        },
      };
    });
  };

  /** Publishes an approved campaign to the ad platform */
  const handlePublishAdsCampaign = async (messageId, campaignId) => {
    const campaign = onlineAds[messageId]?.campaigns?.find(c => c.id === campaignId);
    if (!campaign) return;

    // Get platform tokens from sessionStorage or user state
    const metaToken = sessionStorage.getItem('meta_ads_token');
    const googleToken = sessionStorage.getItem('google_ads_token');

    setOnlineAds(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], submitting: true },
    }));

    try {
      const ADS_PUBLISH_WEBHOOK = "https://workflow.igr-consulting.com/webhook/marketing-ads-publish";
      const resp = await fetch(ADS_PUBLISH_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaigns: [campaign],
          platformTokens: { meta: metaToken, google: googleToken },
        }),
      });
      if (!resp.ok) throw new Error(`Publish webhook error (${resp.status})`);

      const data = await resp.json();
      toast.success(`Campaign published to ${campaign.platform}! Campaign ID: ${data.campaignId || 'N/A'}`);

      setOnlineAds(prev => {
        const updated = [...(prev[messageId]?.campaigns || [])];
        const idx = updated.findIndex(c => c.id === campaignId);
        if (idx >= 0) updated[idx] = { ...updated[idx], status: 'live', campaignId: data.campaignId };
        return {
          ...prev,
          [messageId]: { ...prev[messageId], campaigns: updated, submitting: false },
        };
      });
      setCampaignStatus(prev => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          onlineAds: {
            ...prev[messageId]?.onlineAds,
            published: (prev[messageId]?.onlineAds?.published || 0) + 1,
            status: 'live',
            roas: data.roas || prev[messageId]?.onlineAds?.roas,
          },
        },
      }));
    } catch (err) {
      toast.error(`Publish failed: ${err.message}`);
      setOnlineAds(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], submitting: false },
      }));
    }
  };

  /** Fetches campaign performance (placeholder) */
  const handleFetchAdsPerformance = async (messageId, campaignId) => {
    toast.info('Fetching performance metrics — placeholder (ad platform API integration needed)');
  };

  /** Regenerates an ad campaign */
  const handleRegenerateAdsCampaign = (messageId, campaignId) => {
    toast.info(`Regenerate campaign ${campaignId} — placeholder (backend endpoint needed)`);
  };

  /** Goes back from ads preview to channel selection */
  const handleAdsBack = (messageId) => {
    setOnlineAds(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
    setChannelSelection(prev => ({
      ...prev,
      [messageId]: { open: true, selectedChannels: channelSelection[messageId]?.selectedChannels || [] },
    }));
  };

  // ── SOCIAL MEDIA CHANNEL (existing pipeline, kept for backward compat) ─────

  /** Quick shortcut: opens social media form directly (skips strategy) */
  const handleSocialMediaFormOpen = (messageId) => {
    setMarketingForms(prev => ({
      ...prev,
      [messageId]: { open: true, submitting: false, submitted: false },
    }));
  };

  /** Goes back from social media carousel to channel selection */
  const handleSocialMediaBack = (messageId) => {
    setSocialPosts(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
    setChannelSelection(prev => ({
      ...prev,
      [messageId]: { open: true, selectedChannels: channelSelection[messageId]?.selectedChannels || [] },
    }));
  };

  // ── CAMPAIGN STATUS DASHBOARD ───────────────────────────────────────────────

  /** Opens the unified campaign dashboard for a message */
  const handleOpenCampaignDashboard = (messageId) => {
    setCampaignStatus(prev => ({
      ...prev,
      [messageId]: { ...prev[messageId], dashboardOpen: true },
    }));
  };

  /** Navigates from dashboard to a specific channel view */
  const handleViewChannelFromDashboard = (messageId, channelKey) => {
    if (channelKey === 'offline') {
      setOfflineMarketing(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], open: true },
      }));
    } else if (channelKey === 'onlineAds') {
      setOnlineAds(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], open: true },
      }));
    } else if (channelKey === 'socialMedia') {
      setSocialPosts(prev => ({
        ...prev,
        [messageId]: { ...prev[messageId], open: true },
      }));
    }
  };

  /** Exports all campaign assets (placeholder) */
  const handleExportAllCampaignAssets = (messageId) => {
    toast.info('Export all campaign assets — placeholder (ZIP generation needed)');
  };

  /** Views analytics (placeholder) */
  const handleViewCampaignAnalytics = (messageId) => {
    toast.info('Campaign analytics — placeholder (dashboard integration needed)');
  };

  /** "Post Now" handler — directly publishes a generated post via Postiz */
  const handlePostNow = async (postMsg) => {
    const postizToken = sessionStorage.getItem('postiz_token');
    if (!postizToken) {
      toast.error('Not connected to Postiz. Please go to Performance Marketing to connect.');
      return;
    }
    const pinterestError = getPinterestPostError(postMsg.integrationId, getEffectivePostMediaAll(postMsg).length > 0);
    if (pinterestError) { toast.error(pinterestError); return; }
    const mediaError = getPlatformMediaError(postMsg.platform, getEffectivePostMediaAll(postMsg));
    if (mediaError) { toast.error(mediaError); return; }

    setExtraMessages(prev =>
      prev.map(m => m.id === postMsg.id ? { ...m, postStatus: 'posting', postError: null } : m)
    );

    try {
      const fullCaption = [postMsg.caption, ...postMsg.hashtags].filter(Boolean).join('\n');
      const images = await uploadMediaItems(postizToken, getEffectivePostMediaAll(postMsg));

      const result = await postizAPI.schedulePost(postizToken, {
        text:           fullCaption,
        images,
        integrationIds: [postMsg.integrationId],
        integrations:   connectors,
        pinterestBoardIds,
        publishedAt:    new Date().toISOString(),
        postNow:        true,
      });

      if (result.success) {
        toast.success(`Posted to ${postMsg.platform} successfully!`);
        setExtraMessages(prev =>
          prev.map(m => m.id === postMsg.id ? { ...m, postStatus: 'posted' } : m)
        );
      } else {
        throw new Error(result.error || 'Posting failed');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to post: ${msg}`);
      setExtraMessages(prev =>
        prev.map(m => m.id === postMsg.id ? { ...m, postStatus: 'error', postError: msg } : m)
      );
    }
  };

  /** "Schedule Post" handler — toggles inline date/time picker on the card */
  const handleScheduleGeneratedPost = (postMsg) => {
    setInlineSchedule(prev => {
      const cur = prev[postMsg.id] || {};
      return {
        ...prev,
        [postMsg.id]: {
          open: !cur.open,
          date: cur.date || '',
          time: cur.time || '09:00',
          scheduling: false,
          scheduled: false,
        },
      };
    });
  };

  /** Submits an inline-scheduled post */
  const handleInlineScheduleSubmit = async (postMsg) => {
  const sched = inlineSchedule[postMsg.id];
  if (!sched?.date) { toast.error('Please select a date.'); return; }
  const scheduledAt = new Date(`${sched.date}T${sched.time || '09:00'}`);
  if (scheduledAt <= new Date()) { toast.error('Please select a future date/time.'); return; }
  const pinterestError = getPinterestPostError(postMsg.integrationId, getEffectivePostMediaAll(postMsg).length > 0);
  if (pinterestError) { toast.error(pinterestError); return; }
  const mediaError = getPlatformMediaError(postMsg.platform, getEffectivePostMediaAll(postMsg));
  if (mediaError) { toast.error(mediaError); return; }

  const postizToken = sessionStorage.getItem('postiz_token');
  if (!postizToken) {
    toast.error('Not connected to Postiz. Please go to Performance Marketing to connect.');
    return;
  }

  setInlineSchedule(prev => ({ ...prev, [postMsg.id]: { ...prev[postMsg.id], scheduling: true } }));

  try {
    const fullCaption = [postMsg.caption, ...postMsg.hashtags].filter(Boolean).join('\n');
    const images = await uploadMediaItems(postizToken, getEffectivePostMediaAll(postMsg));

    const result = await postizAPI.schedulePost(postizToken, {
      text: fullCaption,
      images,
      integrationIds: [postMsg.integrationId],
      integrations: connectors,
      pinterestBoardIds,
      publishedAt: scheduledAt.toISOString(),
      postNow: false,
    });

    if (result.success) {
      toast.success(`Scheduled for ${scheduledAt.toLocaleString()}!`);
      setInlineSchedule(prev => ({ ...prev, [postMsg.id]: { ...prev[postMsg.id], scheduling: false, scheduled: true, open: false } }));
      setExtraMessages(prev => prev.map(m => m.id === postMsg.id ? { ...m, postStatus: 'posted' } : m));
    } else {
      throw new Error(result.error || 'Scheduling failed');
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    toast.error(`Failed to schedule: ${msg}`);
    setInlineSchedule(prev => ({ ...prev, [postMsg.id]: { ...prev[postMsg.id], scheduling: false } }));
  }
};

  const handleQuickReply = async (reply) => {
    await sendQuickReply(reply.title, reply.payload);
  };

  const handleFormSubmit = async (formData, formId) => {
    await submitForm(formData, formId);
  };

  const formatTime = (date) => {
    // Handle both Date objects and date strings
    if (!date) return "";
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateLabel = (dateKey) => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (dateKey === today) return "Today";
    if (dateKey === yesterday) return "Yesterday";
    const date = new Date(dateKey);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const groupMessagesByDate = (messages) => {
    return messages.reduce((groups, message) => {
      const dateKey = new Date(message.timestamp || Date.now()).toDateString();
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(message);
      return groups;
    }, {});
  };

  // Load chat from history
  // Load chat from history
  const loadChatFromHistory = async (chat) => {
    console.log("📂 Loading chat from history:", chat.id, "-", chat.title);

    // Close sidebar immediately
    setShowHistory(false);
    setIsHistoryView(true);
    setActiveNav("Home");

    const GENERIC_TITLES = ["Your Conversation", "Conversation", "New Conversation", ""];
    const storedTitle = chat.title?.trim() || "";

    // Show whatever we have immediately
    setConversationTitle(storedTitle || "Conversation");

    // ⚠️ FIX #7: Clear message-related state when switching to old conversation
    setTypingMessages(new Set());
    console.log("🧹 Cleared typingMessages for old conversation");

    const conversationKey = chat.conversationKey || chat.id;

    if (conversationKey) {
      console.log("🌐 Loading messages for key:", conversationKey);

      try {
        const result = await loadMessagesFromHistory(conversationKey);

        if (result.success) {
          console.log("✅ Chat loaded successfully");
          scrollToTop();
          const url = new URL(window.location);
          url.searchParams.set("chatId", conversationKey);
          window.history.pushState({}, "", url);

          // Only attempt title resolution if the stored title is generic
          if (GENERIC_TITLES.includes(storedTitle)) {
            const loadedMessages = result.messages || [];
            const messageCount = loadedMessages.length;

            const applyTitle = (title) => {
              setConversationTitle(title);
              updateChatHistoryTitle(conversationKey, title);
              setChatHistoryRefreshKey(k => k + 1);
            };

            if (messageCount >= 4) {
              // Step 1: try fetching from the database
              const lookupId = chat.vcxConversationId || conversationKey;
              try {
                const res = await getConversationTitle(lookupId, WEBCHAT_CONFIG.uuid);
                const dbTitle = res.data?.conversationName?.trim();
                if (res.success && dbTitle && !GENERIC_TITLES.includes(dbTitle)) {
                  applyTitle(dbTitle);
                  return; // done
                }
              } catch (_) {}

              // Step 2: database had no title — generate one via webhook
              try {
                const recentMessages = loadedMessages.slice(-8).map(m => ({
                  role: m.type === "user" ? "user" : "assistant",
                  message: m.content,
                }));
                const resp = await fetch(
                  "https://workflow.igr-consulting.com/webhook/140630b2-e8fa-4dcd-87e9-772f933dcd47",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ conversation: recentMessages }),
                  }
                );
                if (resp.ok) {
                  const data = await resp.json();
                  const generated = data.output?.[0]?.content?.[0]?.text?.trim();
                  if (generated && !GENERIC_TITLES.includes(generated)) {
                    applyTitle(generated);
                    // Persist to backend too
                    addConversationTitle(lookupId, WEBCHAT_CONFIG.uuid, generated).catch(() => {});
                    return;
                  }
                }
              } catch (_) {}

              // Step 3: webhook also failed — fall back to first user message
              const firstUserMsg = loadedMessages.find(m => m.type === "user");
              if (firstUserMsg?.content) {
                const text = firstUserMsg.content.trim();
                applyTitle(text.length > 60 ? text.substring(0, 60) + "…" : text);
              }
            } else {
              // Fewer than 4 messages — just use the first user message
              const firstUserMsg = loadedMessages.find(m => m.type === "user");
              if (firstUserMsg?.content) {
                const text = firstUserMsg.content.trim();
                applyTitle(text.length > 60 ? text.substring(0, 60) + "…" : text);
              }
            }
          }
        } else {
          console.warn("⚠️ Failed to load chat history:", result.error);
        }
      } catch (err) {
        console.error("❌ Error loading chat:", err);
      }
    } else {
      console.error("❌ Invalid chat object selected", chat);
    }
  };

  const handleTypingComplete = (messageId) => {
    setTypingMessages((prev) => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
  };

  const NavIcon = ({ name }) => {
    const icons = {
      home: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          ></path>
        </svg>
      ),
      discover: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
          ></path>
        </svg>
      ),
      history: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
      ),
      question: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      "bar-chart": (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          ></path>
        </svg>
      ),
      brands: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
    };
    return icons[name] || null;
  };



  // Enhanced message rendering with better design
  const renderMessageContent = (message, isHighlighted = false) => {
    const isTyping = typingMessages.has(message.id);

    // ── POST PREVIEW MESSAGE ────────────────────────────────────────────────
    if (message.type === 'post_preview') {
      const meta = PLATFORM_META_MAP[message.platform] || {
        icon: FaInstagram, color: '#6366f1', label: message.platform,
      };
      const PlatformIcon = meta.icon;
      const isPosting   = message.postStatus === 'posting';
      const isPosted    = message.postStatus === 'posted';
      const isError     = message.postStatus === 'error';
      const sched       = inlineSchedule[message.id] || {};
      const isLinkedIn  = message.platform === 'linkedin';
      const isFacebook  = message.platform === 'facebook';

      const mediaOverride = postMediaOverrides[message.id];
      const allMediaItems = mediaOverride !== undefined
        ? (mediaOverride.items || [])
        : (message.mediaUrl ? [{ url: message.mediaUrl, type: message.mediaType }] : []);
      const carouselIndex = Math.min(mediaOverride?.activeIndex ?? 0, Math.max(0, allMediaItems.length - 1));
      const currentMedia = allMediaItems[carouselIndex] || { url: null, type: null };
      const effectiveMediaUrl  = currentMedia.url;
      const effectiveMediaType = currentMedia.type;
      const mediaError = getPlatformMediaError(message.platform, allMediaItems);

      /* ── Shared caption renderer (caption + inline hashtags) ── */
      const CaptionBlock = () => (
        <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${
          isDarkMode ? 'text-gray-200' : 'text-gray-800'
        }`}>
          {message.caption}
          {message.hashtags.length > 0 && (
            <span style={{ color: isLinkedIn ? '#0A66C2' : isFacebook ? '#1877F2' : meta.color }}>
              {' '}{message.hashtags.join(' ')}
            </span>
          )}
        </p>
      );

      /* ── Carousel media section ── */
      const MediaSection = ({ className = '' }) => {
        const count = allMediaItems.length;
        return (
          <div className={`relative group/media w-full bg-black/5 ${className}`}>
            {count > 0 ? (
              <>
                {/* Current slide */}
                {effectiveMediaType === 'video' ? (
                  <video key={carouselIndex} src={effectiveMediaUrl} className="w-full" controls muted />
                ) : (
                  <img key={carouselIndex} src={effectiveMediaUrl} alt={`Post media ${carouselIndex + 1}`} className="w-full object-cover" />
                )}

                {/* Prev / Next arrows — only when multiple items */}
                {count > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleCarouselNav(message.id, -1)}
                      className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCarouselNav(message.id, 1)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                    </button>
                    {/* Dot indicators */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {allMediaItems.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setPostMediaOverrides(prev => ({ ...prev, [message.id]: { ...prev[message.id], activeIndex: i } }))}
                          className={`w-1.5 h-1.5 rounded-full transition-all ${
                            i === carouselIndex ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                          }`}
                        />
                      ))}
                    </div>
                    {/* Counter badge */}
                    <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-black/60 text-white">
                      {carouselIndex + 1} / {count}
                    </span>
                  </>
                )}

                {/* Hover controls */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/media:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => handleAddPostMedia(message.id)}
                    title="Add media"
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-black/70 text-white hover:bg-black/90 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDiscardPostMedia(message.id)}
                    title="Remove current"
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-red-600/80 text-white hover:bg-red-600 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => handleAddPostMedia(message.id)}
                className={`w-full flex flex-col items-center justify-center gap-2 py-6 text-xs font-medium transition-colors ${
                  isDarkMode ? 'text-gray-400 hover:bg-gray-700/50' : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                Add your own media
              </button>
            )}
            {mediaError && (
              <p className="px-3 py-2 text-xs font-medium text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30">
                {mediaError}
              </p>
            )}
          </div>
        );
      };

      /* ── Inline schedule picker ── */
      const InlineSchedulePicker = () => (
        <div className={`mt-2 p-3 rounded-xl border ${
          isDarkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}>
          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Choose date & time
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="date"
              value={sched.date || ''}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setInlineSchedule(prev => ({ ...prev, [message.id]: { ...prev[message.id], date: e.target.value } }))}
              className={`text-xs px-2 py-1.5 rounded-lg border outline-none ${
                isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
              }`}
            />
            <input
              type="time"
              value={sched.time || '09:00'}
              onChange={e => setInlineSchedule(prev => ({ ...prev, [message.id]: { ...prev[message.id], time: e.target.value } }))}
              className={`text-xs px-2 py-1.5 rounded-lg border outline-none ${
                isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-800'
              }`}
            />
            <button
              onClick={() => handleInlineScheduleSubmit(message)}
              disabled={sched.scheduling}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:brightness-90 active:scale-95 disabled:opacity-60"
              style={{ backgroundColor: meta.color }}
            >
              {sched.scheduling ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {sched.scheduling ? 'Scheduling…' : 'Confirm'}
            </button>
            <button
              onClick={() => setInlineSchedule(prev => ({ ...prev, [message.id]: { ...prev[message.id], open: false } }))}
              className={`text-xs px-2 py-1.5 rounded-lg border transition-all ${
                isDarkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      );

      /* ── Action buttons row (shared) ── */
      const ActionButtons = () => (
        <div className={`px-4 py-2.5 border-t flex flex-col gap-1 ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          {isPosted ? (
            <span className="text-xs font-semibold text-green-500 flex items-center gap-1 py-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
              {sched.scheduled ? 'Scheduled successfully!' : 'Posted successfully!'}
            </span>
          ) : (
            <>
              {isPinterestIntegration(message.integrationId) && (
                <PinterestBoardSelect
                  boards={pinterestBoards[message.integrationId]}
                  value={pinterestBoardIds[message.integrationId]}
                  onChange={(boardId) => setPinterestBoardIds(prev => ({ ...prev, [message.integrationId]: boardId }))}
                  isDarkMode={isDarkMode}
                  disabled={isPosting || sched.scheduling}
                />
              )}
              <div className="flex items-center gap-2">
                <button
                  disabled={isPosting}
                  onClick={() => handlePostNow(message)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all text-white hover:brightness-90 active:scale-95 disabled:opacity-60"
                  style={{ backgroundColor: meta.color }}
                >
                  {isPosting ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                  {isPosting ? 'Posting…' : 'Post Now'}
                </button>
                <button
                  disabled={isPosting}
                  onClick={() => handleScheduleGeneratedPost(message)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border active:scale-95 disabled:opacity-60 ${
                    sched.open
                      ? (isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-200' : 'bg-gray-100 border-gray-400 text-gray-800')
                      : (isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50')
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule Post
                </button>
                {isError && <span className="text-xs text-red-500 ml-1">⚠ {message.postError}</span>}
              </div>
              {sched.open && <InlineSchedulePicker />}
            </>
          )}
        </div>
      );

      /* ════════════════════════════════
         LINKEDIN PREVIEW
         ════════════════════════════════ */
      if (isLinkedIn) {
        return (
          <div className="flex items-start gap-2 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                 style={{ backgroundColor: '#0A66C222', border: '1px solid #0A66C255' }}>
              <FaLinkedin className="w-4 h-4" style={{ color: '#0A66C2' }} />
            </div>
            <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
              <p className="text-xs font-semibold mb-1.5 ml-1" style={{ color: '#0A66C2' }}>
                LinkedIn Post Preview
              </p>
              {/* LinkedIn card */}
              <div className={`rounded-xl overflow-hidden shadow border ${
                isDarkMode ? 'bg-[#1b1f23] border-[#38434f]' : 'bg-white border-[#e0e0e0]'
              }`} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
                {/* Author row */}
                <div className="px-4 pt-3 pb-2 flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                       style={{ background: 'linear-gradient(135deg,#0A66C2,#004182)' }}>
                    {meta.label[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-[#000000e6]'}`}>
                      IGR-Infi-Portal
                    </div>
                    <div className={`text-xs leading-tight truncate ${isDarkMode ? 'text-gray-400' : 'text-[#00000099]'}`}>
                      3d • <svg className="inline w-3 h-3 mb-0.5" fill="currentColor" viewBox="0 0 16 16"><path d="M8 0a8 8 0 100 16A8 8 0 008 0zm0 14.5A6.5 6.5 0 118 1.5a6.5 6.5 0 010 13z"/></svg>
                    </div>
                  </div>
                  <button className="text-xs font-semibold px-3 py-1 rounded-full border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C211] transition-colors flex items-center gap-1">
                    + Follow
                  </button>
                </div>
                {/* Caption */}
                <div className="px-4 pb-2">
                  <CaptionBlock />
                </div>
                {/* Media */}
                <MediaSection />
                {/* LinkedIn reaction bar */}
                <div className={`px-4 py-1 flex items-center gap-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#00000099]'}`}>
                  <span>👍❤️🎉</span>
                  <span className="ml-1">127</span>
                  <span className="ml-auto">335 comments</span>
                </div>
                <div className={`border-t mx-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                {/* LinkedIn action row */}
                <div className={`flex items-center ${isDarkMode ? 'text-gray-400' : 'text-[#00000099]'}`}>
                  {[
                    { icon: '👍', label: 'Like' },
                    { icon: '💬', label: 'Comment' },
                    { icon: '🔁', label: 'Repost' },
                    { icon: '📤', label: 'Send' },
                  ].map(a => (
                    <button key={a.label} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold hover:bg-black/5 transition-colors`}>
                      <span>{a.icon}</span>{a.label}
                    </button>
                  ))}
                </div>
                <ActionButtons />
              </div>
              <div className="flex items-center mt-1 px-1">
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(message.timestamp)}
                </span>
              </div>
            </div>
          </div>
        );
      }

      /* ════════════════════════════════
         FACEBOOK PREVIEW
         ════════════════════════════════ */
      if (isFacebook) {
        return (
          <div className="flex items-start gap-2 mb-4">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                 style={{ backgroundColor: '#1877F222', border: '1px solid #1877F255' }}>
              <FaFacebook className="w-4 h-4" style={{ color: '#1877F2' }} />
            </div>
            <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
              <p className="text-xs font-semibold mb-1.5 ml-1" style={{ color: '#1877F2' }}>
                Facebook Post Preview
              </p>
              {/* Facebook card */}
              <div className={`rounded-xl overflow-hidden shadow border ${
                isDarkMode ? 'bg-[#242526] border-[#3a3b3c]' : 'bg-white border-[#dddfe2]'
              }`} style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
                {/* Author row */}
                <div className="px-4 pt-3 pb-2 flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                       style={{ background: 'linear-gradient(135deg,#1877F2,#0050a0)' }}>
                    T
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold leading-tight ${isDarkMode ? 'text-[#e4e6eb]' : 'text-[#050505]'}`}>
                      Test Page
                    </div>
                    <div className={`text-xs leading-tight flex items-center gap-1 ${isDarkMode ? 'text-[#b0b3b8]' : 'text-[#65676b]'}`}>
                      Published by IGR-Infi-Portal •{' '}
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} at{' '}
                      {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} •{' '}
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 0a10 10 0 100 20A10 10 0 0010 0z"/></svg>
                    </div>
                  </div>
                  <button className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-[#65676b]'} px-1`}>···</button>
                </div>
                {/* Caption */}
                <div className="px-4 pb-2">
                  <CaptionBlock />
                </div>
                {/* Media */}
                <MediaSection />
                {/* Reactions */}
                <div className={`px-4 py-1.5 flex items-center justify-between text-xs ${isDarkMode ? 'text-[#b0b3b8]' : 'text-[#65676b]'}`}>
                  <div className="flex items-center gap-1"><span>👍❤️😮</span><span>127</span></div>
                  <span>335 comments · 24 shares</span>
                </div>
                <div className={`border-t mx-3 ${isDarkMode ? 'border-[#3a3b3c]' : 'border-[#dddfe2]'}`} />
                {/* FB action row */}
                <div className="flex items-center">
                  {[
                    { icon: '👍', label: 'Like' },
                    { icon: '💬', label: 'Comment' },
                    { icon: '↗', label: 'Share' },
                  ].map(a => (
                    <button key={a.label} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors ${
                      isDarkMode ? 'text-[#b0b3b8] hover:bg-[#3a3b3c]' : 'text-[#65676b] hover:bg-[#f2f2f2]'
                    }`}>
                      <span>{a.icon}</span>{a.label}
                    </button>
                  ))}
                </div>
                {/* See insights row */}
                <div className={`px-4 py-2 border-t flex items-center justify-between ${isDarkMode ? 'border-[#3a3b3c]' : 'border-[#dddfe2]'}`}>
                  <button className="text-xs text-[#65676b] hover:underline">See insights and ads</button>
                  <button className="text-xs font-semibold px-3 py-1.5 rounded text-white" style={{ backgroundColor: '#1877F2' }}>
                    Promote post
                  </button>
                </div>
                <ActionButtons />
              </div>
              <div className="flex items-center mt-1 px-1">
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(message.timestamp)}
                </span>
              </div>
            </div>
          </div>
        );
      }

      /* ════════════════════════════════
         GENERIC / OTHER PLATFORM PREVIEW
         ════════════════════════════════ */
      return (
        <div className="flex items-start gap-2 mb-4">
          {/* Bot-style avatar */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm`}
               style={{ backgroundColor: meta.color + '22', border: `1px solid ${meta.color}55` }}>
            <PlatformIcon className="w-4 h-4" style={{ color: meta.color }} />
          </div>

          <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
            {/* Header label */}
            <p className={`text-xs font-semibold mb-1.5 ml-1`} style={{ color: meta.color }}>
              {meta.label} Post Preview
            </p>

            {/* Card */}
            <div className={`rounded-2xl overflow-hidden shadow-sm border ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              {/* Platform header strip */}
              <div className="px-4 py-2.5 flex items-center gap-2"
                   style={{ backgroundColor: meta.color + '18', borderBottom: `1px solid ${meta.color}30` }}>
                <PlatformIcon className="w-4 h-4" style={{ color: meta.color }} />
                <span className="text-xs font-semibold" style={{ color: meta.color }}>
                  {meta.label}
                </span>
              </div>

              {/* Media preview */}
              <MediaSection className="bg-black/10" />

              {/* Caption */}
              <div className="px-4 py-3">
                <CaptionBlock />
              </div>

              <ActionButtons />
            </div>

            {/* Timestamp */}
            <div className="flex items-center mt-1 px-1">
              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {formatTime(message.timestamp)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (message.type === "user") {
      // USER MESSAGE - Right side
      const copyText = message.isHtml ? stripHtmlToText(message.content) : message.content;
      const isEditing = editingMessageId === message.id;
      return (
        <div className="flex justify-end items-start gap-2 mb-4 group/user">
          <div className="flex-1 flex justify-end min-w-0">
            <div className="max-w-[85%] md:max-w-[70%] w-full">
              {isEditing ? (
                /* ── Inline edit mode ── */
                <div className={`rounded-2xl shadow-sm p-2 ${isDarkMode ? "bg-cyan-700" : "bg-cyan-500"}`}>
                  <textarea
                    autoFocus
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (editingText.trim()) {
                          editAndResend(message.id, editingText.trim());
                          setEditingMessageId(null);
                          setEditingText("");
                        }
                      }
                      if (e.key === "Escape") {
                        setEditingMessageId(null);
                        setEditingText("");
                      }
                    }}
                    rows={Math.max(2, editingText.split("\n").length)}
                    className="w-full bg-transparent text-white text-sm leading-relaxed resize-none outline-none placeholder-white/60"
                  />
                  <div className="flex justify-end gap-2 mt-1.5">
                    <button
                      onClick={() => { setEditingMessageId(null); setEditingText(""); }}
                      className="text-xs px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (editingText.trim()) {
                          editAndResend(message.id, editingText.trim());
                          setEditingMessageId(null);
                          setEditingText("");
                        }
                      }}
                      disabled={!editingText.trim()}
                      className="text-xs px-3 py-1 rounded-lg bg-white text-cyan-600 font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Normal display mode ── */
                <div
                  className={`px-3 py-2 rounded-2xl ${isDarkMode
                    ? "bg-cyan-600 text-white"
                    : "bg-cyan-500 text-white"
                    } shadow-sm break-words ${isHighlighted ? 'ring-4 ring-cyan-300 shadow-xl transition-all duration-500 scale-[1.02]' : 'transition-all duration-500'}`}
                >
                  <div className="text-sm leading-relaxed break-words chat-message-content">
                    <MessageContent
                      content={message.content}
                      isHtml={message.isHtml}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </div>
              )}

              {!isEditing && (
                <div className="flex items-center justify-end gap-2 mt-0.5 px-1">
                  <span
                    className={`text-xs opacity-0 group-hover/user:opacity-100 group-focus-within/user:opacity-100 transition-opacity ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                    title={new Date(message.timestamp).toLocaleString()}
                  >
                    {formatTime(message.timestamp)}
                  </span>
                  <MessageActions
                    message={message}
                    isDarkMode={isDarkMode}
                    onCopy={() => navigator.clipboard.writeText(copyText)}
                    onEdit={() => {
                      setEditingMessageId(message.id);
                      setEditingText(message.content);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* User Avatar */}
          <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">
            {user?.avatar || "U"}
          </div>
        </div>
      );
    }

    // ASSISTANT MESSAGE - Left side
    // Check if this is an Admin Reply
    // Wait, the example says: "senderId": "TES8846" for the admin reply.
    // So if senderId is NOT the generic bot ID... 
    // Actually `questionFor` is the strongest signal.

    // Find referenced question if exists
    const referencedQuestion = message.questionFor
      ? chatMessages.find(m => m.id === message.questionFor)
      : null;

    return (
      <div className="flex items-start gap-2 mb-4 group/assistant">
        {/* Bot Avatar or Admin Avatar */}
        <div
          className={`w-8 h-8 ${isDarkMode
            ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30"
            : "bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300"
            } rounded-full flex items-center justify-center flex-shrink-0 shadow-sm relative`}
        >
          {message.questionFor ? (
            // Admin Icon
            <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ) : (
            // Bot Sparkle Icon
            <svg
              className="w-4 h-4 text-cyan-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
              <path d="M18 4L18.5 6.5L21 7L18.5 7.5L18 10L17.5 7.5L15 7L17.5 6.5L18 4Z" />
            </svg>
          )}
        </div>

        <div className="flex-1 min-w-0 relative">

          {/* Admin Reply Context */}
          {message.questionFor && (
            <div className={`mb-1 pl-2 text-xs font-semibold ${isDarkMode ? "text-cyan-400" : "text-cyan-700"}`}>
              Replied by Admin {message.senderId && message.senderId !== "TES8846" ? `(${message.senderId})` : ""}
            </div>
          )}

          {referencedQuestion && (
            <div
              onClick={() => handleScrollToMessage(message.questionFor)}
              className={`mb-2 p-2 rounded-lg border-l-4 border-cyan-500 ml-1 text-xs opacity-90 cursor-pointer transition-all hover:opacity-100 ${isDarkMode ? "bg-gray-700/50 hover:bg-gray-600 shadow-sm text-gray-300" : "bg-gray-100 hover:bg-gray-200 shadow-sm text-gray-700"}`}
              title="Click to view original question"
            >
              <div className="font-semibold mb-0.5 flex items-center gap-1">
                <svg className="w-3 h-3 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                Replying to:
              </div>
              <div className="line-clamp-2 italic">"{referencedQuestion.content}"</div>
            </div>
          )}

          {/* Message Content */}
          <div
            id={`message-content-${message.id}`}
            className={`px-3 py-2 rounded-2xl max-w-[85%] md:max-w-[70%] ${isDarkMode
              ? "bg-gray-800 text-gray-200"
              : "bg-gray-200 text-gray-800"
              } shadow-sm relative overflow-hidden ${isHighlighted ? 'ring-4 ring-cyan-500 shadow-xl transition-all duration-500 scale-[1.02]' : 'transition-all duration-500'}`}
          >
            {/* Blur Overlay & CTA */}


            <div className={`text-sm leading-relaxed break-words chat-message-content ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              <MessageContent
                content={message.content}
                isHtml={message.isHtml}
                isDarkMode={isDarkMode}
                blurInfo={message.blurInfo}
                isTyping={isTyping}
                messageId={message.id}
                onTypingComplete={handleTypingComplete}
                onScroll={() => scrollToBottom(true)}
              />
            </div>

            {/* Image rendering */}
            {message.imageUrl && (
              <img
                src={message.imageUrl}
                alt="Bot response"
                className="mt-3 rounded-lg max-w-full h-auto"
              />
            )}

            {/* Video rendering */}
            {message.videoUrl && (
              <video controls className="mt-3 rounded-lg max-w-full h-auto">
                <source src={message.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}

            {/* Audio rendering */}
            {message.audioUrl && (
              <audio controls className="mt-3 max-w-full">
                <source src={message.audioUrl} type="audio/mp3" />
                Your browser does not support the audio tag.
              </audio>
            )}

            {/* Location rendering */}
            {message.location &&
              message.location.latitude &&
              message.location.longitude && (
                <div className="mt-3">
                  <p className="font-medium">
                    {message.location.title || "Location"}
                  </p>
                  <p className="text-sm opacity-75">
                    {message.location.address || "Address not available"}
                  </p>
                  <iframe
                    src={`https://www.google.com/maps?q=${message.location.latitude},${message.location.longitude}&hl=en;z=14&output=embed`}
                    width="100%"
                    height="250"
                    className="mt-2 rounded-lg border-0"
                    title="Location"
                  />
                </div>
              )}

            {/* 🚀 BLUR OVERLAY (Moved to end) */}
            {
              message.blurInfo && (
                <div
                  className="absolute inset-x-0 bottom-0 z-50 overflow-hidden cursor-pointer rounded-b-2xl"
                  style={{
                    // Only show overlay if there is actually content to obscure
                    display: message.blurInfo.required > 0 ? 'block' : 'none',
                    // Calculate absolute position based on visible content ratio vs TOTAL (including garbage)
                    // visibleLen = content.length * (1 - required/total)
                    // totalLen = Max(content.length, MIN_MASKED_LENGTH)
                    // top % = (visibleLen / totalLen) * 100
                    top: (() => {
                      const hiddenRatio = Math.min(1, Math.max(0, message.blurInfo.required / (message.blurInfo.total || 1)));
                      const visibleRatio = 1 - hiddenRatio;
                      const contentLen = message.content ? message.content.length : 0;
                      const visibleLen = contentLen * visibleRatio;
                      const totalLen = Math.max(contentLen, MIN_MASKED_LENGTH);
                      const topPct = (visibleLen / (totalLen || 1)) * 100;
                      return `${Math.min(Math.floor(topPct), 60)}%`;
                    })()
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPaymentModal(true);
                  }}
                  title="Click to Upgrade"
                >
                  {/* 1. The Blur/Fade Layer */}
                  <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                      background: `linear-gradient(to bottom, transparent, ${isDarkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)'} 30%)`,
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                    }}
                  />

                  {/* 2. The Subscription Card */}
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-[280px] p-6 rounded-2xl shadow-2xl transform transition-transform hover:scale-105 bg-white border border-gray-100">
                      <div className="text-center space-y-3">
                        <h3 className="text-xl font-bold text-gray-900">
                          Upgrade to Premium
                        </h3>
                        <p className="text-sm text-gray-500 leading-relaxed">
                          You've reached the free limit. Unlock full responses and features .
                        </p>

                        <div className="pt-2">
                          <button
                            className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold text-sm shadow-lg hover:shadow-rose-500/30 hover:brightness-110 transition-all"
                          >
                            Upgrade Plan
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          </div >

          {/* Timestamp and Actions */}
          <div className="flex items-center gap-2 mt-0.5 px-1">
            <span
              className={`text-xs opacity-0 group-hover/assistant:opacity-100 group-focus-within/assistant:opacity-100 transition-opacity ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
              title={new Date(message.timestamp).toLocaleString()}
            >
              {formatTime(message.timestamp)}
            </span>

            <MessageActions
              message={message}
              isDarkMode={isDarkMode}
              isAssistant
              currentPlan={currentPlan}
              user={user}
              onCopy={() => {
                const textToCopy = message.isHtml ? stripHtmlToText(message.content) : message.content;
                navigator.clipboard.writeText(textToCopy);
              }}
              onRegenerate={() => regenerateResponse(message.id)}
              onThumbsUp={() => onThumbsUp(message.id)}
              onThumbsDown={() => onThumbsDown(message.id)}
              onAskAdmin={() => {
                setTargetBotMessage(message);
                setShowAskQuestionModal(true);
              }}
              onSchedule={() => {
                setSchedulePostData({
                  mediaUrl: message.imageUrl || message.videoUrl,
                  caption: message.content || ""
                });
                setShowSchedulePostModal(true);
              }}
              onGeneratePDF={() => {
                if (currentPlan?.includes("free")) {
                  setShowPaymentModal(true);
                  return;
                }
                handleGeneratePDF(message.id);
              }}
              onGeneratePPT={() => {
                if (currentPlan?.includes("CMO")) {
                  handleGeneratePPT(message.id);
                  return;
                }
                setShowPaymentModal(true);
              }}
              onGenerateMarketingStrategy={() => handleGenerateMarketingStrategy(message.id)}
              onDownloadReport={() => handleDownloadReport(message.id)}
              strategyForms={strategyForms}
              marketingForms={marketingForms}
            />
          </div>

            {marketingForms[message.id]?.open && (
              <MarketingStrategyForm
                isDarkMode={isDarkMode}
                connectors={availableConnectors}
                submitting={marketingForms[message.id]?.submitting}
                strategy={marketingStrategies[message.id]?.strategy}
                onSubmit={(formData) => handleSocialPostsFormSubmit(message.id, formData)}
                onCancel={() =>
                  setMarketingForms(prev => ({
                    ...prev,
                    [message.id]: { open: false, submitting: false, submitted: false },
                  }))
                }
              />
            )}

            {/* Social Media Post Preview (one-by-one) */}
            {socialPosts[message.id] && (
              <SocialMediaPostPreview
                posts={socialPosts[message.id]?.posts || []}
                pendingDays={socialPosts[message.id]?.pendingDays || []}
                isDarkMode={isDarkMode}
                generating={socialPosts[message.id]?.generating}
                campaignName={marketingStrategies[message.id]?.strategy?.executiveSummary?.substring?.(0, 50) || ''}
                onApprove={(postId) => handleApproveSocialPost(message.id, postId)}
                renderBoardPicker={(post) => {
                  const integrationId = resolveSocialPostIntegrationId(post);
                  if (!isPinterestIntegration(integrationId)) return null;
                  return (
                    <PinterestBoardSelect
                      boards={pinterestBoards[integrationId]}
                      value={pinterestBoardIds[integrationId]}
                      onChange={(boardId) => setPinterestBoardIds(prev => ({ ...prev, [integrationId]: boardId }))}
                      isDarkMode={isDarkMode}
                      disabled={post.status === 'approving' || post.status === 'regenerating'}
                    />
                  );
                }}
                onRegenerate={(postId) => handleRegenerateSocialPost(message.id, postId)}
                onBack={() => handleSocialMediaBack(message.id)}
              />
            )}

            {/* ════════════════════════════════════════════════════════════════
                NEW: MARKETING STRATEGY V2 — Strategy Layer & Channels
                ════════════════════════════════════════════════════════════════ */}

            {/* Step 1: Strategy Generation Form */}
            {strategyForms[message.id]?.open && (
              <StrategyGenerationForm
                isDarkMode={isDarkMode}
                submitting={strategyForms[message.id]?.submitting}
                onSubmit={(formData) => handleStrategyFormSubmit(message.id, formData)}
                onCancel={() => setStrategyForms(prev => ({ ...prev, [message.id]: { open: false, submitting: false, submitted: false } }))}
              />
            )}

            {/* Step 2: Strategy Document Viewer */}
            {marketingStrategies[message.id]?.open && !marketingStrategies[message.id]?.approved && (
              <MarketingStrategyDocument
                strategy={marketingStrategies[message.id]?.strategy || {}}
                isDarkMode={isDarkMode}
                onSectionEdit={(section, content) => handleStrategySectionEdit(message.id, section, content)}
                onRegenerateSection={(section) => handleRegenerateStrategySection(message.id, section)}
                onApprove={() => handleApproveStrategy(message.id)}
                onDiscard={() => handleRejectStrategy(message.id)}
                submitting={marketingStrategies[message.id]?.submitting}
                regeneratingSection={marketingStrategies[message.id]?.regeneratingSection}
              />
            )}

            {/* Step 3: Channel Selection Modal */}
            {channelSelection[message.id]?.open && (
              <ChannelSelectionModal
                strategy={marketingStrategies[message.id]?.strategy || {}}
                isDarkMode={isDarkMode}
                selectedChannels={channelSelection[message.id]?.selectedChannels || []}
                onToggle={(channel) => handleChannelToggle(message.id, channel)}
                onContinue={() => handleChannelContinue(message.id)}
                onBack={() => handleChannelBack(message.id)}
              />
            )}

            {/* Step 4: Offline Marketing Form */}
            {offlineForms[message.id]?.open && (
              <OfflineMarketingForm
                isDarkMode={isDarkMode}
                strategy={marketingStrategies[message.id]?.strategy || {}}
                submitting={offlineForms[message.id]?.submitting}
                onSubmit={(formData) => handleOfflineFormSubmit(message.id, formData)}
                onCancel={() => {
                  setOfflineForms(prev => ({ ...prev, [message.id]: { open: false, submitting: false } }));
                  setChannelSelection(prev => ({ ...prev, [message.id]: { open: true, selectedChannels: channelSelection[message.id]?.selectedChannels || [] } }));
                }}
              />
            )}

            {/* Step 5: Offline Marketing Preview */}
            {offlineMarketing[message.id]?.open && (
              <OfflineMarketingPreview
                materials={offlineMarketing[message.id]?.materials || []}
                pendingTypes={offlineMarketing[message.id]?.pendingTypes || []}
                isDarkMode={isDarkMode}
                onApprove={(materialId) => handleApproveOfflineMaterial(message.id, materialId)}
                onDownload={(materialId, format) => handleDownloadOfflineMaterial(message.id, materialId, format)}
                onRegenerate={(materialId) => handleRegenerateOfflineMaterial(message.id, materialId)}
                onRegenerateAll={() => handleRegenerateAllOfflineMaterials(message.id)}
                onBack={() => handleOfflineBack(message.id)}
                submitting={offlineMarketing[message.id]?.submitting}
              />
            )}

            {/* Step 6: Online Ads Form */}
            {adsForms[message.id]?.open && (
              <OnlineAdsForm
                isDarkMode={isDarkMode}
                strategy={marketingStrategies[message.id]?.strategy || {}}
                submitting={adsForms[message.id]?.submitting}
                onSubmit={(formData) => handleAdsFormSubmit(message.id, formData)}
                onCancel={() => {
                  setAdsForms(prev => ({ ...prev, [message.id]: { open: false, submitting: false } }));
                  setChannelSelection(prev => ({ ...prev, [message.id]: { open: true, selectedChannels: channelSelection[message.id]?.selectedChannels || [] } }));
                }}
              />
            )}

            {/* Step 7: Online Ads Preview */}
            {onlineAds[message.id]?.open && (
              <OnlineAdsPreview
                campaigns={onlineAds[message.id]?.campaigns || []}
                isDarkMode={isDarkMode}
                onApproveCampaign={(campaignId) => handleApproveAdsCampaign(message.id, campaignId)}
                onPublishCampaign={(campaignId) => handlePublishAdsCampaign(message.id, campaignId)}
                onRegenerateCampaign={(campaignId) => handleRegenerateAdsCampaign(message.id, campaignId)}
                onFetchPerformance={(campaignId) => handleFetchAdsPerformance(message.id, campaignId)}
                onBack={() => handleAdsBack(message.id)}
                submitting={onlineAds[message.id]?.submitting}
              />
            )}

            {/* Step 8: Campaign Status Dashboard */}
            {campaignStatus[message.id]?.strategyApproved && !strategyForms[message.id]?.open && !marketingStrategies[message.id]?.open && !channelSelection[message.id]?.open && !offlineForms[message.id]?.open && !offlineMarketing[message.id]?.open && !adsForms[message.id]?.open && !onlineAds[message.id]?.open && !marketingForms[message.id]?.open && !socialPosts[message.id] && (
              <CampaignStatusDashboard
                status={campaignStatus[message.id] || {}}
                isDarkMode={isDarkMode}
                onViewChannel={(channel) => handleViewChannelFromDashboard(message.id, channel)}
                onDownloadAll={() => handleExportAllCampaignAssets(message.id)}
                onViewAnalytics={() => handleViewCampaignAnalytics(message.id)}
              />
            )}

          {
            message.quickReplies && message.quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {message.quickReplies.map((reply, idx) => {
                  const isLocked = (!currentPlan || currentPlan === "free_plan") && (reply.title === "Generate PDF" || reply.title === "Generate PPT");

                  return (
                    <div key={idx} className="relative group/tooltip">
                      <button
                        onClick={() => {
                          if (isLocked) {
                            setShowPaymentModal(true);
                            return;
                          }
                          handleQuickReply(reply);
                        }}
                        className={`px-4 py-2 flex items-center gap-2 cursor-pointer ${isDarkMode
                          ? isLocked ? "bg-gray-800 text-gray-500 border border-gray-700 hover:bg-gray-700" : "bg-gray-700 hover:bg-gray-600 text-gray-300"
                          : isLocked ? "bg-gray-100 text-gray-400 border border-gray-200 hover:bg-gray-200" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                          } rounded-full text-sm transition-all shadow-sm`}
                      >
                        {isLocked && (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        )}
                        {reply.title}
                      </button>
                      {isLocked && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowPaymentModal(true);
                          }}
                          className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity cursor-pointer z-10 ${isDarkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"
                            }`}
                        >
                          Paid Service
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          }

          {/* Carousel Cards */}
          {
            message.cards && message.cards.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                {message.cards.map((card, idx) => (
                  <div
                    key={idx}
                    className={`${isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                      } border rounded-xl p-3 shadow-sm`}
                  >
                    {card.image && (
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-40 object-cover rounded-lg mb-2"
                      />
                    )}
                    <h4
                      className={`font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-900"
                        } mb-1.5`}
                    >
                      {card.title}
                    </h4>
                    <p
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"
                        } mb-2`}
                    >
                      {card.subtitle}
                    </p>
                    {card.buttons && card.buttons.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {card.buttons.map((button, btnIdx) => {
                          const isLocked = (!currentPlan || currentPlan === "free_plan") && (button.title === "Generate PDF" || button.title === "Generate PPT");

                          return (
                            <div key={btnIdx} className="relative group/tooltip">
                              <button
                                onClick={() => {
                                  if (isLocked) {
                                    setShowPaymentModal(true);
                                    return;
                                  }
                                  button.url
                                    ? window.open(button.url, "_blank")
                                    : handleQuickReply({
                                      title: button.title,
                                      payload: button.payload,
                                    });
                                }}
                                className={`px-3 py-1.5 flex items-center gap-1.5 ${isLocked
                                  ? `cursor-pointer ${isDarkMode ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`
                                  : "bg-cyan-500 hover:bg-cyan-600 text-white"
                                  } rounded-lg text-sm transition-all`}
                              >
                                {isLocked && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                )}
                                {button.title}
                              </button>
                              {isLocked && (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPaymentModal(true);
                                  }}
                                  className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover/tooltip:opacity-100 transition-opacity cursor-pointer z-10 ${isDarkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"
                                    }`}
                                >
                                  Paid Service
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          }

          {/* Form */}
          {
            message.formFields && (
              <FormRenderer
                fields={message.formFields}
                description={message.formDescription}
                onSubmit={(data) =>
                  handleFormSubmit(data, message.formId || message.id)
                }
                isDarkMode={isDarkMode}
              />
            )
          }
        </div >
      </div >
    );
  };

  const handleQuestionSubmit = async (data) => {
    try {
      // prioritization: 1. URL param (chatId) 2. userSession.vcxConversationId 3. userSession.conversationId
      const urlParams = new URLSearchParams(window.location.search);
      const chatIdFromUrl = urlParams.get("chatId");

      const targetConversationId = chatIdFromUrl || userSession?.vcxConversationId || userSession?.conversationId;
      const currentUserId = userSession?.userId || user?.userId || "anonymous";

      const payload = {
        uuid: WEBCHAT_CONFIG.uuid,
        questionFor: data.botResponseContent || "General Question",
        userId: currentUserId,
        userName: user?.name || user?.username || "Anonymous",
        userEmail: user?.email || "no-email@provided.com",
        question: data.question,
        category: data.category || "support",
        source: "webchat",
        priority: data.priority || "medium",
        withChat: data.includeContext,
        conversationId: targetConversationId,
        messageId: data.botResponseId
      };

      const response = await fetch('https://chat.versalence.info/interactions/infinium/postQuestion', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${CHAT_AUTH_TOKEN}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        // toast.success("Question sent to admin successfully!");
        setShowAskQuestionModal(false);
        setAskQuestionPrefill("");
        setShowQuestionSuccessModal(true);
      } else {
        console.error("Ask Question API error:", response.status);
        toast.error("Failed to send question. Please try again.");
      }
    } catch (error) {
      console.error("Error sending question:", error);
      toast.error("An error occurred while sending your question.");
    }
  };

  // Check for admin replies (Mock Notification System)
  useEffect(() => {
    const checkReplies = () => {
      const savedQuestions = localStorage.getItem('user_questions');
      if (savedQuestions) {
        try {
          const questions = JSON.parse(savedQuestions);
          const repliedAndUnread = questions.filter(q => q.status === 'Replied' && !q.read);

          if (repliedAndUnread.length > 0) {
            repliedAndUnread.forEach(q => {
              toast.info(
                <div onClick={() => {
                  setActiveNav('Questions');
                  // Mark as read
                  const updated = questions.map(item =>
                    item.id === q.id ? { ...item, read: true } : item
                  );
                  localStorage.setItem('user_questions', JSON.stringify(updated));
                  window.dispatchEvent(new Event('storage'));
                }} className="cursor-pointer">
                  <strong>Admin replied:</strong>
                  <p className="text-sm truncate">{q.adminReply}</p>
                </div>,
                { autoClose: 10000, toastId: `reply-${q.id}` }
              );
            });
          }
        } catch (e) {
          console.error("Error checking replies", e);
        }
      }
    };

    // Check initially and set interval
    checkReplies();
    const interval = setInterval(checkReplies, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className={`flex h-screen ${isDarkMode ? "bg-gray-950" : "bg-gray-50"
        } overflow-hidden`}
    >
      {/* Mobile Sidebar Backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:relative w-64 lg:w-20 h-full ${isDarkMode
            ? "bg-gray-900 border-gray-800"
            : "bg-white border-gray-200"
          } border-r flex flex-col items-center py-6 z-50 transition-transform duration-300`}
      >
        {/* Logo */}
        <div className="mb-5">
          <div className="w-14 h-14 flex items-center justify-center cursor-pointer transform hover:scale-110 transition-all duration-300 drop-shadow-lg bg-white rounded-full p-2 border border-gray-200">
            <img
              src="/logo.png"
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* New Chat Button */}
        <button
          onClick={() => {
            resetComposerState();
            resetChat();
            setIsHistoryView(false);
            setConversationTitle("New Conversation");
            setTitleGenerated(false);
            setActiveNav("Home");
            setExtraMessages([]);
            setSocialPosts({});
            if (window.innerWidth < 1024) setIsSidebarOpen(false);

            // Clear URL param
            const url = new URL(window.location);
            url.searchParams.delete("chatId");
            window.history.pushState({}, "", url);
          }}
          className={`mb-5 p-2.5 flex items-center gap-2.5 text-sm ${isDarkMode
            ? "bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white"
            : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900"
            } rounded-xl transition-all group w-52 lg:w-auto justify-start lg:justify-center`}
          title="New Chat"
        >
          <svg
            className="w-6 h-6 transform group-hover:rotate-90 transition-transform lg:mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            ></path>
          </svg>
          <span className="lg:hidden font-medium">New Chat</span>
        </button>

        {/* Navigation Items */}
        <nav className="flex-1 flex flex-col gap-2 w-full px-4 lg:px-0 items-center">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => {
                if (item.name === "History") {
                  setShowHistory(!showHistory);
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                } else {
                  setIsHistoryView(false);
                  setActiveNav(item.name);
                  setShowHistory(false);
                  if (window.innerWidth < 1024) setIsSidebarOpen(false);
                  if (item.name === "Home") {
                    resetComposerState();
                    resetChat();
                    setConversationTitle("New Conversation");
                    setTitleGenerated(false);
                    setExtraMessages([]);
                    setSocialPosts({});
                  }
                  // Clear URL param for main nav items
                  const url = new URL(window.location);
                  url.searchParams.delete("chatId");
                  window.history.pushState({}, "", url);
                }
              }}
              className={`relative p-3 rounded-xl transition-all group w-full lg:w-auto flex items-center gap-3 justify-start lg:justify-center ${activeNav === item.name
                ? isDarkMode
                  ? "bg-gray-800 text-white"
                  : "bg-gray-200 text-gray-900"
                : isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-800"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              title={item.label}
            >
              <NavIcon name={item.icon} />
              <span className="lg:hidden font-medium">{item.label}</span>
              {activeNav === item.name && (
                <span className={`absolute left-0 lg:left-auto lg:-right-0.5 top-1/2 -translate-y-1/2 w-1 h-8 ${isDarkMode ? "bg-gray-600" : "bg-gray-400"} rounded-r-full lg:rounded-l-full rounded-l-none`}></span>
              )}
            </button>
          ))}
        </nav>

        {/* Bottom Actions - Perfectly aligned with nav items */}
        <div className="mt-auto flex flex-col gap-2 w-full px-4 lg:px-0 items-center">
          {/* Theme Toggle Button - Same styling as nav items */}
          <button
            onClick={toggleTheme}
            className={`relative p-3 rounded-xl transition-all group w-full lg:w-auto flex items-center gap-3 justify-start lg:justify-center ${isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-gray-800"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                ></path>
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                ></path>
              </svg>
            )}
            <span className="lg:hidden font-medium">{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
          </button>

          {/* Upgrade Button - Same styling as nav items */}
          <button
            onClick={() => setShowPaymentModal(true)}
            className={`relative p-3 rounded-xl transition-all group w-full lg:w-auto flex items-center gap-3 justify-start lg:justify-center ${isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-gray-800"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            title="Upgrade"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              ></path>
            </svg>
            <span className="lg:hidden font-medium">Upgrade</span>
            <span className="absolute top-3 right-3 lg:-top-0.5 lg:-right-0.5 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          {/* Notifications Button */}
          <button
            onClick={() => setShowNotificationsModal(true)}
            className={`relative p-3 rounded-xl transition-all group w-full lg:w-auto flex items-center gap-3 justify-start lg:justify-center ${isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-gray-800"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            title="Notifications"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className="lg:hidden font-medium">Notifications</span>
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 lg:-top-0.5 lg:-right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Account Button - Same styling as nav items */}
          <Link
            to="/profile"
            className={`relative p-3 rounded-xl transition-all group w-full lg:w-auto flex items-center gap-3 justify-start lg:justify-center ${isDarkMode
              ? "text-gray-400 hover:text-white hover:bg-gray-800"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            title="Account"
          >
            <div className="w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-xs shrink-0">
              {user?.avatar || "U"}
            </div>
            <span className="lg:hidden font-medium">Profile</span>
            {user?.isPro && (
              <div className="absolute top-3 right-3 lg:-top-0.5 lg:-right-0.5 w-3 h-3 bg-cyan-400 rounded-full flex items-center justify-center">
                <span className="text-[8px] font-bold text-gray-900">P</span>
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ease-in-out w-full overflow-hidden ${showHistory ? "lg:ml-[320px]" : ""}`}
      >
        {/* Mobile Header with Hamburger */}
        <div className="lg:hidden p-4 flex items-center justify-between border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => {
              setIsSidebarOpen(true);
              if (window.innerWidth < 1024) setShowHistory(false);
            }}
            className={`p-2 rounded-lg ${isDarkMode ? "text-white hover:bg-gray-800" : "text-gray-900 hover:bg-gray-100"}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="font-semibold text-lg">{activeNav === 'Questions' ? 'My Questions' : activeNav}</div>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>

        {/* Connection Status */}
        {!isChatConnected && activeNav !== "Discover" && activeNav !== "Questions" && (
          <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2 text-center">
            <span className="text-yellow-600 dark:text-yellow-400 text-sm">
              Connecting to chat...
            </span>
          </div>
        )}

        {/* Response Delayed Notice */}
        {responseDelayed && activeNav !== "Discover" && activeNav !== "Questions" && (
          <div className="bg-cyan-500/10 border-b border-cyan-500/20 px-4 py-2 flex items-center justify-center gap-3">
            <span className="text-cyan-600 dark:text-cyan-400 text-sm">
              Still working on the response. This may take longer for deeper tasks.
            </span>
            <button
              type="button"
              onClick={handleRetryLastResponse}
              className="text-sm font-medium px-3 py-1 rounded-md bg-cyan-500 text-white hover:bg-cyan-600 transition-colors"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={dismissResponseDelayed}
              className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-200 transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Content Area Switch */}
        {activeNav === "Discover" ? (
          <ComingSoon isDarkMode={isDarkMode} />
        ) : activeNav === "Questions" ? (
          <MyQuestions isDarkMode={isDarkMode} user={user} />
        ) : activeNav === "Performance Marketing" ? (
          <PerformanceMarketing isDarkMode={isDarkMode} user={user} currentPlan={currentPlan} activeRole={activeRole} />
        ) : activeNav === "Brands" ? (
          <BrandManagement isDarkMode={isDarkMode} onBrandsChange={syncBrands} />
        ) : chatMessages.length === 0 && !isHistoryView ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto">
            <div className="w-full max-w-5xl px-4">
              {/* Hero Video Panel */}
              <HeroVideoPanel isDarkMode={isDarkMode} />

              {/* Suggested Prompts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => {
                      setSearchQuery(prompt);
                      setTimeout(() => handleSearch({ preventDefault: () => {} }), 0);
                    }}
                    className={`text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                      isDarkMode
                        ? "border-gray-700 text-gray-300 hover:border-cyan-500/50 hover:bg-gray-800"
                        : "border-gray-200 text-gray-600 hover:border-cyan-400 hover:bg-cyan-50"
                    }`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>

              {/* Search Input */}
              <div className="mb-5">
                <ChatInputBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSubmit={handleSearch}
                  isDarkMode={isDarkMode}
                  disabled={insufficientCredits || planExpired || isGeneratingPost}
                  isLoading={isChatTyping || isGeneratingPost}
                  onStop={stopResponse}
                  canStop={canStop}
                  placeholder={
                    planExpired ? "Plan expired. Please renew."
                    : insufficientCredits ? "Insufficient credits to chat"
                    : selectedPlatforms.length > 0 ? "Describe your post requirements..."
                    : "Ask anything..."
                  }
                  minHeight="112px"
                  currentPlan={currentPlan}
                  availableConnectors={availableConnectors}
                  selectedPlatforms={selectedPlatforms}
                  togglePostPlatform={togglePostPlatform}
                  postMediaType={postMediaType}
                  setPostMediaType={setPostMediaType}
                  businessLogo={businessLogo}
                  onLogoUpload={handleLogoUpload}
                  onLogoRemove={handleLogoRemove}
                  hasConversation={false}
                  brands={brands}
                  selectedBrandId={selectedBrandId}
                  onSelectBrand={setSelectedBrandId}
                />
              </div>

              {/* Disclaimer */}
              <div
                className={`mt-6 text-center text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
              >
                <p>
                  <strong>Disclaimer:</strong> IGR-Infi-Portal provides AI-generated market insights for informational purposes only. These outputs do not constitute financial, investment, legal, or professional advice. Please verify all information independently before making business decisions.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Chat Messages - Reduced horizontal padding */}
            <div
              ref={messagesContainerRef}
              onScroll={handleScrollContainer}
              className="flex-1 overflow-y-auto relative"
            >
              <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
                {/* Conversation Title */}
                <div
                  className={`mb-6 pb-4 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"
                    }`}
                >
                  <h2
                    className={`text-2xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    {conversationTitle}
                  </h2>
                </div>

                {/* Messages */}
                {isHistoryLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                        isDarkMode ? "bg-gray-800 text-cyan-400" : "bg-gray-100 text-cyan-600"
                      }`}
                    >
                      <svg
                        className="w-8 h-8 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Loading chat history...
                    </p>
                  </div>
                ) : chatMessages.length === 0 && isHistoryView ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                        isDarkMode ? "bg-gray-800 text-gray-500" : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                      No messages in this conversation yet.
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                      Type a message to continue the chat.
                    </p>
                  </div>
                ) : (
                  Object.entries(
                    groupMessagesByDate(
                      [...chatMessages, ...extraMessages].sort(
                        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                      )
                    )
                  ).map(([dateKey, dateMessages]) => (
                    <div key={dateKey}>
                      <div className="flex justify-center my-4">
                        <span
                          className={`text-xs px-3 py-1 rounded-full ${
                            isDarkMode
                              ? "bg-gray-800 text-gray-400"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {formatDateLabel(dateKey)}
                        </span>
                      </div>
                      {dateMessages.map((message) => (
                        <div
                          id={`message-item-${message.id}`}
                          key={message.id}
                          className="scroll-mt-24"
                        >
                          {renderMessageContent(message, highlightedMessageId === message.id)}
                        </div>
                      ))}
                    </div>
                  ))
                )}

                {/* Post Generating Placeholder */}
                {isGeneratingPost && (
                  <PostGeneratingPlaceholder isDarkMode={isDarkMode} />
                )}

                {/* Typing Indicator */}
                {isChatTyping && (
                  <div className="flex items-start gap-3 mb-6">
                    {/* Bot Avatar */}
                    <div
                      className={`w-8 h-8 ${isDarkMode
                        ? "bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30"
                        : "bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300"
                        } rounded-full flex items-center justify-center flex-shrink-0 shadow-sm`}
                    >
                      <svg
                        className="w-4 h-4 text-cyan-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
                        <path d="M18 4L18.5 6.5L21 7L18.5 7.5L18 10L17.5 7.5L15 7L17.5 6.5L18 4Z" />
                      </svg>
                    </div>

                    <TypingIndicator isDarkMode={isDarkMode} />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to Bottom Button */}
              {showScrollButton && (
                <button
                  type="button"
                  onClick={() => {
                    scrollToBottom();
                    setShowScrollButton(false);
                  }}
                  className={`absolute bottom-4 right-4 md:right-8 p-2 rounded-full shadow-lg border transition-all ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                      : "bg-white border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
                  aria-label="Scroll to bottom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
              )}
            </div>

            {/* Input Area */}
            <div
              className={`border-t ${isDarkMode
                ? "border-gray-800 bg-gray-900/50"
                : "border-gray-200 bg-white/50"
                } backdrop-blur-sm p-4`}
            >
              <div className="max-w-6xl mx-auto px-4">
                <ChatInputBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSubmit={handleSearch}
                  isDarkMode={isDarkMode}
                  disabled={insufficientCredits || planExpired || isGeneratingPost}
                  isLoading={isChatTyping || isGeneratingPost}
                  onStop={stopResponse}
                  canStop={canStop}
                  placeholder={
                    planExpired ? "Plan expired. Please renew."
                    : insufficientCredits ? "Insufficient credits to chat"
                    : selectedPlatforms.length > 0 ? "Describe your post requirements..."
                    : "Ask a follow-up question..."
                  }
                  showAttachment={false}
                  fileInputRef={fileInputRef}
                  onFileSelect={handleFileUpload}
                  isUploading={isUploading}
                  currentPlan={currentPlan}
                  availableConnectors={availableConnectors}
                  selectedPlatforms={selectedPlatforms}
                  togglePostPlatform={togglePostPlatform}
                  postMediaType={postMediaType}
                  setPostMediaType={setPostMediaType}
                  businessLogo={businessLogo}
                  onLogoUpload={handleLogoUpload}
                  onLogoRemove={handleLogoRemove}
                  hasConversation={chatMessages.length > 0}
                  brands={brands}
                  selectedBrandId={selectedBrandId}
                  onSelectBrand={setSelectedBrandId}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat History Sidebar */}
      <ChatHistory
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        isDarkMode={isDarkMode}
        onSelectChat={loadChatFromHistory}
        currentConversationId={userSession?.conversationId}
        onDeleteBotpressMessages={() => deleteBotpressMessages(userSession?.userId)}
        refreshKey={chatHistoryRefreshKey}
      />

      {/* Account Settings Modal */}
      {
        showAccountSettings && (
          <AccountSettings
            user={user}
            onClose={() => setShowAccountSettings(false)}
            onLogout={onLogout}
            isDarkMode={isDarkMode}
          />
        )
      }

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        isDarkMode={isDarkMode}
        currentPlan={currentPlan}
        remainingTokens={remainingTokens}
        totalTokens={totalTokens}
        user={user}
        onPaymentSuccess={fetchActiveSubscription}
        activePlanVersionId={activePlanVersionId}
      />

      <PaymentModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        isDarkMode={isDarkMode}
        currentPlan={currentPlan}
        remainingTokens={remainingTokens}
        totalTokens={totalTokens}
        user={user}
        onPaymentSuccess={fetchActiveSubscription}
        isUpgrade={true}
        activePlanVersionId={activePlanVersionId}
      />

      {/* Verification Modal */}
      {
        verifyingPayment && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm">
            <div className={`p-8 rounded-2xl shadow-2xl flex flex-col items-center justify-center max-w-sm w-full mx-4 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>

              {verificationStatus === 'processing' && (
                <>
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}>Verifying Payment</h3>
                  <p className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{verificationMessage}</p>
                </>
              )}

              {verificationStatus === 'success' && (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-[bounce_1s_ease-in-out]">
                    <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className={`text-2xl font-bold mb-2 text-green-500`}>Success!</h3>
                  <p className={`text-center mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{verificationMessage}</p>
                </>
              )}

              {verificationStatus === 'error' && (
                <>
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className={`text-2xl font-bold mb-2 text-red-500`}>Verification Failed</h3>
                  <p className={`text-center mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{verificationMessage}</p>
                  <button
                    onClick={() => setVerifyingPayment(false)}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </button>
                </>
              )}

            </div>
          </div>
        )
      }

      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        isDarkMode={isDarkMode}
      />

      <FeedbackSuccessModal
        isOpen={feedbackSuccessModalOpen}
        onClose={() => setFeedbackSuccessModalOpen(false)}
        isDarkMode={isDarkMode}
      />

      <AskQuestionModal
        isOpen={showAskQuestionModal}
        onClose={() => { setShowAskQuestionModal(false); setAskQuestionPrefill(""); }}
        onSubmit={handleQuestionSubmit}
        botResponse={targetBotMessage}
        isDarkMode={isDarkMode}
        prefillMessage={askQuestionPrefill}
      />

      <QuestionSuccessModal
        isOpen={showQuestionSuccessModal}
        onClose={() => setShowQuestionSuccessModal(false)}
        isDarkMode={isDarkMode}
      />

      <NotificationsModal
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        isDarkMode={isDarkMode}
        notifications={notifications}
        onMarkSeen={handleMarkNotificationsSeen}
      />

      {/* Report Download Modal */}
      {reportModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
            isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          }`}>
            {/* Close */}
            <button
              onClick={() => setReportModal({ open: false, loading: false, result: null, topic: "" })}
              className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors ${
                isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-700" : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? "bg-cyan-500/15 border border-cyan-500/30" : "bg-cyan-50 border border-cyan-200"
              }`}>
                <svg className="w-5 h-5 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className={`font-bold text-base ${isDarkMode ? "text-white" : "text-gray-900"}`}>Full Report</h3>
                <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Based on your query</p>
              </div>
            </div>

            {/* Query */}
            <div className={`mb-4 px-3 py-2 rounded-xl text-xs ${
              isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-50 text-gray-600"
            }`}>
              <span className={`font-semibold mr-1.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Query:</span>
              {reportModal.topic}
            </div>

            {/* Loading */}
            {reportModal.loading && (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <svg className="w-8 h-8 text-cyan-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Searching report library...</p>
              </div>
            )}

            {/* Error */}
            {!reportModal.loading && reportModal.result?.error && (
              <div className={`flex items-center gap-3 py-6 px-3 rounded-xl ${
                isDarkMode ? "bg-red-500/10 border border-red-500/20" : "bg-red-50 border border-red-100"
              }`}>
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-700"}`}>
                  Something went wrong while searching for a report. Please try again.
                </p>
              </div>
            )}

            {/* No report found: covers both null link and empty bestMatchFiles array */}
            {!reportModal.loading && !reportModal.result?.error && !reportModal.result?.bestMatchFileLink && !(reportModal.result?.bestMatchFiles?.length > 0) && (
              <div className={`flex flex-col items-center justify-center py-8 gap-3 rounded-xl ${
                isDarkMode ? "bg-gray-800/60" : "bg-gray-50"
              }`}>
                <svg className={`w-10 h-10 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>No report found</p>
                <p className={`text-xs text-center max-w-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  We couldn't find a matching report in our library for this topic. Try rephrasing your query.
                </p>
                <button
                  onClick={() => {
                    setReportModal({ open: false, loading: false, result: null, topic: "" });
                    setAskQuestionPrefill(`I couldn't find a report on: "${reportModal.topic}". Could you please provide or point me to a relevant report on this topic?`);
                    setShowAskQuestionModal(true);
                  }}
                  className="mt-1 flex items-center gap-1.5 text-xs font-medium text-cyan-500 hover:text-cyan-400 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
                  </svg>
                  Request this report from Admin
                </button>
              </div>
            )}

            {/* Report found — handles both single-file and multi-file responses */}
            {!reportModal.loading && !reportModal.result?.error && (reportModal.result?.bestMatchFileLink || reportModal.result?.bestMatchFiles?.length > 0) && (() => {
              const r = reportModal.result;
              const isHigh = r.confidence === "high";
              // Normalise to a list: new format uses bestMatchFiles[], old format has a single link
              const files = Array.isArray(r.bestMatchFiles) && r.bestMatchFiles.length > 0
                ? r.bestMatchFiles
                : [{ bestMatchFileName: r.bestMatchFileName, bestMatchFileLink: r.bestMatchFileLink }];
              return (
                <div className={`rounded-xl border p-4 ${
                  isHigh
                    ? isDarkMode ? "bg-emerald-500/10 border-emerald-500/25" : "bg-emerald-50 border-emerald-200"
                    : isDarkMode ? "bg-amber-500/10 border-amber-500/25" : "bg-amber-50 border-amber-200"
                }`}>
                  {/* Confidence badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      isHigh
                        ? isDarkMode ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"
                        : isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"
                    }`}>
                      {isHigh ? "✓ High Confidence" : "⚠ Low Confidence"}
                    </span>
                    {files.length > 1 && (
                      <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {files.length} reports found
                      </span>
                    )}
                  </div>

                  {/* File list */}
                  <div className="flex flex-col gap-2 mb-4">
                    {files.map((f, i) => (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${
                        isHigh
                          ? isDarkMode ? "bg-emerald-500/8 border-emerald-500/20" : "bg-emerald-50 border-emerald-200"
                          : isDarkMode ? "bg-amber-500/8 border-amber-500/20" : "bg-amber-50 border-amber-200"
                      }`}>
                        <svg className={`w-4 h-4 flex-shrink-0 ${
                          isHigh ? "text-emerald-500" : isDarkMode ? "text-amber-400" : "text-amber-600"
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <p className={`flex-1 text-sm font-medium leading-snug min-w-0 ${
                          isDarkMode ? "text-gray-200" : "text-gray-800"
                        }`}>{f.bestMatchFileName || "Report"}</p>
                        <a
                          href={f.bestMatchFileLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all active:scale-[0.97] ${
                            isHigh ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" />
                          </svg>
                          Download
                        </a>
                      </div>
                    ))}
                  </div>

                  {/* Low confidence note */}
                  {!isHigh && (
                    <p className={`text-xs mb-3 ${
                      isDarkMode ? "text-amber-300/80" : "text-amber-700"
                    }`}>
                      {files.length > 1 ? "These" : "This"} may not be an exact match. Review the title before downloading.
                    </p>
                  )}

                  {/* Contact admin */}
                  <button
                    onClick={() => {
                      const names = files.map(f => f.bestMatchFileName).filter(Boolean).join(", ");
                      setReportModal({ open: false, loading: false, result: null, topic: "" });
                      setAskQuestionPrefill(`I was searching for a report on: "${reportModal.topic}". Found: ${names || "no exact match"}. Could you help me find a more relevant report?`);
                      setShowAskQuestionModal(true);
                    }}
                    className="mt-3 flex items-center justify-center gap-1.5 w-full text-xs font-medium text-cyan-500 hover:text-cyan-400 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
                    </svg>
                    Need something else? Contact Admin
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <SchedulePostModal
        isOpen={showSchedulePostModal}
        onClose={() => setShowSchedulePostModal(false)}
        isDarkMode={isDarkMode}
        token={sessionStorage.getItem("postiz_token")}
        mediaUrl={schedulePostData.mediaUrl}
        initialCaption={schedulePostData.caption}
        connectors={connectors}
        onScheduleSuccess={() => {
          setShowSchedulePostModal(false);
          setSchedulePostData({ mediaUrl: null, caption: "" });
        }}
      />

      {/* Share Modal - for sharing chat messages to social media */}
      <SchedulePostModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareMessageText("");
        }}
        isDarkMode={isDarkMode}
        token={sessionStorage.getItem("postiz_token")}
        initialCaption={shareMessageText}
        connectors={connectors}
        onScheduleSuccess={() => {
          setShowShareModal(false);
          setShareMessageText("");
        }}
      />

      {/* Hidden file input for post media replacement */}
      <input
        ref={postMediaInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={handlePostMediaFileSelect}
      />

    </div >
  );
};

// Form Renderer Component
const FormRenderer = ({ fields, description, onSubmit, isDarkMode }) => {
  const [formData, setFormData] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div
      className={`mt-4 p-6 ${isDarkMode
        ? "bg-gray-800 border-gray-700"
        : "bg-gray-50 border-gray-200"
        } border rounded-xl shadow-sm`}
    >
      <div className="mb-4">
        <h4
          className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"
            } mb-2`}
        >
          📋 {description || "Please fill out this form"}
        </h4>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {fields &&
          fields.map((field, idx) => (
            <div key={idx}>
              <label
                htmlFor={field.id}
                className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-2`}
              >
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type={field.type || "text"}
                id={field.id}
                name={field.id}
                placeholder={field.placeholder || ""}
                required={field.required}
                value={formData[field.id] || ""}
                onChange={(e) =>
                  setFormData({ ...formData, [field.id]: e.target.value })
                }
                className={`w-full px-4 py-2 ${isDarkMode
                  ? "bg-gray-900 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
                  } border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500`}
              />
            </div>
          ))}

        <button
          type="submit"
          className="w-full px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-medium transition-all text-sm"
        >
          Submit
        </button>
      </form>


    </div>
  );
};

// ── MARKETING STRATEGY FORM ────────────────────────────────────────────────
const PLATFORM_OPTIONS = ['instagram', 'facebook', 'linkedin', 'twitter', 'youtube', 'pinterest'];

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Platform color mapping — soft pastel tints for selected state
const PLATFORM_COLORS = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2',
  x: '#000000',
  youtube: '#FF0000',
  pinterest: '#E60023',
};

const MarketingStrategyForm = ({ isDarkMode, connectors, submitting, onSubmit, onCancel, strategy }) => {
  const [days, setDays] = useState(strategy?.campaignDuration ? String(strategy.campaignDuration) : '7');
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [userReqs, setUserReqs] = useState('');
  const [audience, setAudience] = useState('');
  const [publishTime, setPublishTime] = useState('09:00');

  const platformList = connectors && connectors.length > 0
    ? connectors.map(c => (c.identifier || c.type || c.name || '').toLowerCase()).filter(Boolean)
    : PLATFORM_OPTIONS;

  const togglePlatform = (p) => {
    setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const toggleDayOfWeek = (d) => {
    setSelectedDaysOfWeek(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!days || parseInt(days) < 1) return;
    if (selectedPlatforms.length === 0) return;
    onSubmit({
      numberOfDays: days,
      daysOfWeek: selectedDaysOfWeek,
      platforms: selectedPlatforms,
      userRequirements: userReqs,
      targetAudience: audience,
      publishTime,
    });
  };

  // Infinium design tokens
  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-900/80' : 'bg-white';
  const inputBorder = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputFocus = 'focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const labelClass = `block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const chipBase = `text-xs px-3 py-1.5 rounded-full border font-medium transition-all duration-200 cursor-pointer`;

  return (
    <div className="flex items-start gap-2 mb-4">
      {/* Bot Avatar — Infinium sparkle icon */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
        : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
      }`}>
        <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
          <path d="M18 4L18.5 6.5L21 7L18.5 7.5L18 10L17.5 7.5L15 7L17.5 6.5L18 4Z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
        {/* Assistant label */}
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
          ✨ AI Marketing Strategist
        </p>

        {/* Main bubble */}
        <div className={`relative px-4 py-3 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          {/* Close X button — top right */}
          <button
            type="button"
            onClick={onCancel}
            className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 z-10 ${isDarkMode
              ? 'text-gray-400 hover:text-white hover:bg-gray-700/80'
              : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200/80'
            }`}
            aria-label="Close"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header with sparkle */}
          <div className="flex items-center gap-2 mb-4 pr-6">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode
              ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20'
              : 'bg-gradient-to-br from-cyan-100 to-blue-100'
            }`}>
              <svg className="w-3.5 h-3.5 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
                <path d="M18 4L18.5 6.5L21 7L18.5 7.5L18 10L17.5 7.5L15 7L17.5 6.5L18 4Z" />
              </svg>
            </div>
            <h4 className={`text-sm font-bold ${textPrimary}`}>Generate Social Media Posts</h4>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Number of Days + Publish Time — same row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Number of Days <span className="text-red-500">*</span></label>
                <input
                  type="number" min="1" max="365" required
                  value={days} onChange={e => setDays(e.target.value)}
                  placeholder="e.g. 7"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`}
                />
              </div>
              <div>
                <label className={labelClass}>Daily Publish Time <span className="text-red-500">*</span></label>
                <input
                  type="time" required
                  value={publishTime} onChange={e => setPublishTime(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`}
                />
                <p className={`text-xs mt-1 ${textMuted}`}>
                  Posts will be scheduled starting from tomorrow at this time.
                </p>
              </div>
            </div>

            {/* Days of Week */}
            <div>
              <label className={labelClass}>
                Days of Week
                <span className={`ml-1.5 font-normal ${textMuted}`}>(leave empty to post every day)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(d => {
                  const isSelected = selectedDaysOfWeek.includes(d);
                  return (
                    <button
                      key={d} type="button"
                      onClick={() => toggleDayOfWeek(d)}
                      className={`${chipBase} ${isSelected
                        ? 'bg-cyan-50 border-cyan-300 text-cyan-700 shadow-sm scale-105'
                        : isDarkMode
                          ? 'bg-gray-700/60 border-gray-600 text-gray-300 hover:border-cyan-500/50 hover:bg-gray-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-cyan-400 hover:bg-cyan-50/50'
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              {(() => {
                const numDays = parseInt(days) || 0;
                const numSelected = selectedDaysOfWeek.length;
                if (numSelected === 0 && numDays > 0) {
                  return (
                    <p className={`text-xs mt-1.5 ${textMuted}`}>
                      Posting every day — {numDays} post{numDays !== 1 ? 's' : ''} total.
                    </p>
                  );
                }
                if (numSelected > 0 && numDays > 0) {
                  const weeksNeeded = Math.ceil(numDays / numSelected);
                  const pattern = Array.from({ length: Math.min(numDays, 14) }, (_, i) => selectedDaysOfWeek[i % numSelected]).join(', ');
                  const overflowWarning = numDays < numSelected;
                  return (
                    <div className="mt-1.5 space-y-1">
                      <p className={`text-xs ${textMuted}`}>
                        {numDays} post{numDays !== 1 ? 's' : ''} across {numSelected} day{numSelected !== 1 ? 's' : ''}/week
                        {weeksNeeded > 1 ? ` (~${weeksNeeded} weeks)` : ''} — repeating: {pattern}{numDays > 14 ? '…' : ''}
                      </p>
                      {overflowWarning && (
                        <p className={`text-xs font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                          ⚠️ You selected {numSelected} days/week but only {numDays} post{numDays !== 1 ? 's' : ''} — only the first {numDays} day{numDays !== 1 ? 's' : ''} ({selectedDaysOfWeek.slice(0, numDays).join(', ')}) will be used.
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Social Media Platforms */}
            <div>
              <label className={labelClass}>Social Media Platforms <span className="text-red-500">*</span></label>
              <div className="flex flex-wrap gap-2">
                {platformList.map(p => {
                  const isSelected = selectedPlatforms.includes(p);
                  const platformColor = PLATFORM_COLORS[p] || '#6366f1';
                  return (
                    <button
                      key={p} type="button"
                      onClick={() => togglePlatform(p)}
                      className={`${chipBase} flex items-center gap-1.5 ${isSelected
                        ? 'border-transparent'
                        : isDarkMode
                          ? 'bg-gray-700/60 border-gray-600 text-gray-300 hover:border-gray-500'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      style={isSelected ? {
                        backgroundColor: `${platformColor}18`,
                        color: platformColor,
                        borderColor: `${platformColor}40`,
                        boxShadow: `0 1px 4px ${platformColor}15`
                      } : {}}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: isSelected ? platformColor : `${platformColor}80` }}
                      />
                      <span className="capitalize">{p}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* User Requirements */}
            <div>
              <label className={labelClass}>User Requirements</label>
              <textarea
                rows="3"
                value={userReqs} onChange={e => setUserReqs(e.target.value)}
                placeholder="Describe your campaign goals, tone, specific messages..."
                className={`w-full px-3 py-2 text-sm rounded-xl border resize-none ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`}
              />
            </div>

            {/* Target Audience — Coming Soon */}
            <div>
              <label className={`${labelClass} opacity-60`}>
                Target Audience
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>Coming Soon</span>
              </label>
              <input
                type="text"
                disabled
                value=""
                placeholder="Coming Soon..."
                className={`w-full px-3 py-2 text-sm rounded-xl border cursor-not-allowed opacity-60 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-100 border-gray-200'} ${textPrimary} placeholder-gray-400 transition-all duration-200`}
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={submitting || selectedPlatforms.length === 0 || !days}
                className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 active:scale-[0.98] hover:shadow-md hover:shadow-cyan-500/15 hover:brightness-105"
                style={{
                  background: 'linear-gradient(135deg, #22d3ee, #60a5fa)',
                }}
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span>Generate Strategy</span>
                  </>
                )}
              </button>
              <button
                type="button" onClick={onCancel}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] ${isDarkMode
                  ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* IGR-Infi-Portal branding micro-label */}
        <div className="flex items-center mt-1 px-1">
          <span className={`text-[10px] ${textMuted}`}>
            ⚡ Powered by Infinium
          </span>
        </div>
      </div>
    </div>
  );
};
// ════════════════════════════════════════════════════════════════════════════
// NEW INLINE FORM COMPONENTS — Marketing Strategy V2
// ════════════════════════════════════════════════════════════════════════════

const BUDGET_OPTIONS = ['No Budget', 'Under $1K', '$1K–$5K', '$5K–$20K', '$20K+'];
const TONE_OPTIONS = ['Professional', 'Casual', 'Playful', 'Bold', 'Empathetic', 'Technical'];

const StrategyGenerationForm = ({ isDarkMode, submitting, onSubmit, onCancel }) => {
  const [campaignName, setCampaignName] = useState('');
  const [businessGoals, setBusinessGoals] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [campaignDuration, setCampaignDuration] = useState('30');
  const [toneOfVoice, setToneOfVoice] = useState('');
  const [competitors, setCompetitors] = useState('');
  const [userRequirements, setUserRequirements] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!campaignName.trim() || !businessGoals.trim() || !targetAudience.trim()) return;
    onSubmit({
      campaignName,
      businessGoals,
      targetAudience,
      budgetRange,
      campaignDuration,
      toneOfVoice,
      competitors,
      userRequirements,
    });
  };

  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-900/80' : 'bg-white';
  const inputBorder = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputFocus = 'focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const labelClass = `block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
        : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
      }`}>
        <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
          ✨ AI Marketing Strategist — Strategy Builder
        </p>
        <div className={`relative px-4 py-3 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          <button type="button" onClick={onCancel} className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 z-10 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700/80' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200/80'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="flex items-center gap-2 mb-4 pr-6">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20' : 'bg-gradient-to-br from-cyan-100 to-blue-100'}`}>
              <svg className="w-3.5 h-3.5 text-cyan-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" /></svg>
            </div>
            <h4 className={`text-sm font-bold ${textPrimary}`}>Generate Marketing Strategy</h4>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Campaign Name <span className="text-red-500">*</span></label>
              <input type="text" required value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="e.g., Q3 Product Launch"
                className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div>
              <label className={labelClass}>Business Goals <span className="text-red-500">*</span></label>
              <textarea rows="2" required value={businessGoals} onChange={e => setBusinessGoals(e.target.value)} placeholder="What do you want to achieve?"
                className={`w-full px-3 py-2 text-sm rounded-xl border resize-none ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div>
              <label className={labelClass}>Target Audience <span className="text-red-500">*</span></label>
              <textarea rows="2" required value={targetAudience} onChange={e => setTargetAudience(e.target.value)} placeholder="Describe your ideal customer..."
                className={`w-full px-3 py-2 text-sm rounded-xl border resize-none ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Budget Range</label>
                <select value={budgetRange} onChange={e => setBudgetRange(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`}>
                  <option value="">Select...</option>
                  {BUDGET_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Campaign Duration (days)</label>
                <input type="number" min="1" value={campaignDuration} onChange={e => setCampaignDuration(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Tone of Voice</label>
                <select value={toneOfVoice} onChange={e => setToneOfVoice(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`}>
                  <option value="">Select...</option>
                  {TONE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Competitor Names</label>
                <input type="text" value={competitors} onChange={e => setCompetitors(e.target.value)} placeholder="Competitor A, Competitor B"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Special Requirements</label>
              <textarea rows="2" value={userRequirements} onChange={e => setUserRequirements(e.target.value)} placeholder="Any specific focus, constraints, or notes..."
                className={`w-full px-3 py-2 text-sm rounded-xl border resize-none ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={submitting || !campaignName.trim() || !businessGoals.trim() || !targetAudience.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 active:scale-[0.98] hover:shadow-md hover:shadow-cyan-500/15 hover:brightness-105"
                style={{ background: 'linear-gradient(135deg, #22d3ee, #60a5fa)' }}>
                {submitting ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg><span>Generating...</span></>
                ) : (
                  <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg><span>Generate Strategy</span></>
                )}
              </button>
              <button type="button" onClick={onCancel}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] ${isDarkMode ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}>
                Cancel
              </button>
            </div>
          </form>
          <div className="flex items-center mt-1 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const OFFLINE_MATERIAL_TYPES = ['Print', 'Billboard', 'Flyer', 'Brochure', 'Poster', 'Standee', 'Banner', 'Hoarding'];

const OfflineMarketingForm = ({ isDarkMode, strategy, submitting, onSubmit, onCancel }) => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [dimensions, setDimensions] = useState('');
  const [quantity, setQuantity] = useState('');
  const [location, setLocation] = useState('');
  const [brandColors, setBrandColors] = useState('');
  const [specialReqs, setSpecialReqs] = useState('');

  const toggleType = (t) => {
    setSelectedTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedTypes.length === 0) return;
    onSubmit({ materialTypes: selectedTypes, dimensions, quantity, location, brandColors, specialRequirements: specialReqs });
  };

  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-900/80' : 'bg-white';
  const inputBorder = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputFocus = 'focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const labelClass = `block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const chipBase = `text-xs px-3 py-1.5 rounded-full border font-medium transition-all duration-200 cursor-pointer`;

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30'
        : 'bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-300'
      }`}>
        <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
          📦 Offline Marketing — Material Config
        </p>
        <div className={`relative px-4 py-3 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          <button type="button" onClick={onCancel} className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 z-10 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700/80' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200/80'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="flex items-center gap-2 mb-4 pr-6">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20' : 'bg-gradient-to-br from-amber-100 to-orange-100'}`}>
              <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" /></svg>
            </div>
            <h4 className={`text-sm font-bold ${textPrimary}`}>Offline Marketing Materials</h4>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Material Types <span className="text-red-500">*</span></label>
              <div className="flex flex-wrap gap-2">
                {OFFLINE_MATERIAL_TYPES.map(t => {
                  const isSelected = selectedTypes.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleType(t)}
                      className={`${chipBase} ${isSelected ? 'bg-amber-50 border-amber-300 text-amber-700 shadow-sm scale-105' : isDarkMode ? 'bg-gray-700/60 border-gray-600 text-gray-300 hover:border-amber-500/50 hover:bg-gray-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-amber-400 hover:bg-amber-50/50'}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Dimensions</label>
                <input type="text" value={dimensions} onChange={e => setDimensions(e.target.value)} placeholder="e.g., A4, 20ft x 10ft"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
              </div>
              <div>
                <label className={labelClass}>Quantity</label>
                <input type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="Print run count"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Location / Venue</label>
              <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Where will this be displayed?"
                className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div>
              <label className={labelClass}>Brand Colors</label>
              <input type="text" value={brandColors} onChange={e => setBrandColors(e.target.value)} placeholder="Hex codes or 'use brand palette'"
                className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div>
              <label className={labelClass}>Special Requirements</label>
              <textarea rows="2" value={specialReqs} onChange={e => setSpecialReqs(e.target.value)} placeholder="Paper type, finish, lamination..."
                className={`w-full px-3 py-2 text-sm rounded-xl border resize-none ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={submitting || selectedTypes.length === 0}
                className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 active:scale-[0.98] hover:shadow-md hover:shadow-amber-500/15 hover:brightness-105"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #ea580c)' }}>
                {submitting ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg><span>Generating...</span></>
                ) : (
                  <><span>Generate Materials</span></>
                )}
              </button>
              <button type="button" onClick={onCancel}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] ${isDarkMode ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}>
                Cancel
              </button>
            </div>
          </form>
          <div className="flex items-center mt-1 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AD_PLATFORM_OPTIONS = ['Meta (Facebook/Instagram)', 'Google Ads', 'LinkedIn Ads'];
const CAMPAIGN_OBJECTIVES = ['Awareness', 'Traffic', 'Conversions', 'Leads', 'App Installs'];
const CTA_OPTIONS = ['Learn More', 'Sign Up', 'Buy Now', 'Download', 'Contact Us'];

const OnlineAdsForm = ({ isDarkMode, strategy, submitting, onSubmit, onCancel }) => {
  const [adPlatforms, setAdPlatforms] = useState([]);
  const [objective, setObjective] = useState('');
  const [dailyBudget, setDailyBudget] = useState('');
  const [totalBudget, setTotalBudget] = useState('');
  const [cta, setCta] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [retargeting, setRetargeting] = useState(false);
  const [lookalike, setLookalike] = useState(false);
  const [roasTarget, setRoasTarget] = useState('');
  const [abVariants, setAbVariants] = useState('2');

  const togglePlatform = (p) => {
    setAdPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (adPlatforms.length === 0 || !objective) return;
    onSubmit({
      adPlatforms,
      campaignObjective: objective,
      budgetPerDay: dailyBudget,
      totalBudget,
      cta,
      landingPageUrl: landingUrl,
      retargeting,
      lookalike,
      roasTarget,
      abTestVariants: abVariants,
    });
  };

  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-900/80' : 'bg-white';
  const inputBorder = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputFocus = 'focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const labelClass = `block text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const chipBase = `text-xs px-3 py-1.5 rounded-full border font-medium transition-all duration-200 cursor-pointer`;

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30'
        : 'bg-gradient-to-br from-emerald-100 to-green-100 border border-emerald-300'
      }`}>
        <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
        </svg>
      </div>
      <div className="flex-1 min-w-0 max-w-[85%] md:max-w-[70%]">
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
          🎯 Online Ads — Campaign Config
        </p>
        <div className={`relative px-4 py-3 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          <button type="button" onClick={onCancel} className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 z-10 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700/80' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200/80'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="flex items-center gap-2 mb-4 pr-6">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20' : 'bg-gradient-to-br from-emerald-100 to-green-100'}`}>
              <svg className="w-3.5 h-3.5 text-emerald-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" /></svg>
            </div>
            <h4 className={`text-sm font-bold ${textPrimary}`}>Online Ads Campaign</h4>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Ad Platforms <span className="text-red-500">*</span></label>
              <div className="flex flex-wrap gap-2">
                {AD_PLATFORM_OPTIONS.map(p => {
                  const isSelected = adPlatforms.includes(p);
                  return (
                    <button key={p} type="button" onClick={() => togglePlatform(p)}
                      className={`${chipBase} ${isSelected ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm scale-105' : isDarkMode ? 'bg-gray-700/60 border-gray-600 text-gray-300 hover:border-emerald-500/50 hover:bg-gray-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-emerald-400 hover:bg-emerald-50/50'}`}>
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Campaign Objective <span className="text-red-500">*</span></label>
                <select required value={objective} onChange={e => setObjective(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`}>
                  <option value="">Select...</option>
                  {CAMPAIGN_OBJECTIVES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>CTA Button</label>
                <select value={cta} onChange={e => setCta(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`}>
                  <option value="">Select...</option>
                  {CTA_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Daily Budget (₹)</label>
                <input type="number" min="0" value={dailyBudget} onChange={e => setDailyBudget(e.target.value)} placeholder="500"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
              </div>
              <div>
                <label className={labelClass}>Total Budget (₹)</label>
                <input type="number" min="0" value={totalBudget} onChange={e => setTotalBudget(e.target.value)} placeholder="15000"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Landing Page URL</label>
              <input type="url" value={landingUrl} onChange={e => setLandingUrl(e.target.value)} placeholder="https://..."
                className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>ROAS Target</label>
                <input type="number" min="0" step="0.1" value={roasTarget} onChange={e => setRoasTarget(e.target.value)} placeholder="3.0"
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} placeholder-gray-400 transition-all duration-200`} />
              </div>
              <div>
                <label className={labelClass}>A/B Test Variants</label>
                <input type="number" min="1" max="5" value={abVariants} onChange={e => setAbVariants(e.target.value)}
                  className={`w-full px-3 py-2 text-sm rounded-xl border ${inputBg} ${inputBorder} ${inputFocus} ${textPrimary} transition-all duration-200`} />
              </div>
            </div>
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 text-sm cursor-pointer ${textPrimary}`}>
                <input type="checkbox" checked={retargeting} onChange={e => setRetargeting(e.target.checked)} className="w-4 h-4 rounded accent-emerald-500" />
                <span>Enable Retargeting</span>
              </label>
              <label className={`flex items-center gap-2 text-sm cursor-pointer ${textPrimary}`}>
                <input type="checkbox" checked={lookalike} onChange={e => setLookalike(e.target.checked)} className="w-4 h-4 rounded accent-emerald-500" />
                <span>Enable Lookalike</span>
              </label>
            </div>
            <div className="flex gap-2 pt-1">
              <button type="submit" disabled={submitting || adPlatforms.length === 0 || !objective}
                className="flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 active:scale-[0.98] hover:shadow-md hover:shadow-emerald-500/15 hover:brightness-105"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                {submitting ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg><span>Generating...</span></>
                ) : (
                  <><span>Generate Campaigns</span></>
                )}
              </button>
              <button type="button" onClick={onCancel}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] ${isDarkMode ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}>
                Cancel
              </button>
            </div>
          </form>
          <div className="flex items-center mt-1 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
